// art.jsx — Magic Sheep art system, ported from the Claude Design prototype.
//
// The prototype was many <script type="text/babel"> files sharing ONE global
// scope and cross-referencing each other by bare name (and via window.*). We
// concatenate them near-verbatim into this single ES module so those top-level
// declarations still share scope exactly as before. esbuild transforms the JSX
// to our h() string-runtime (app/javascript/lib/jsx_runtime.js).
//
// Rewritten vs. the prototype:
//   • board.jsx (React hooks) → Board/BoardSection markup here + the `board`
//     Stimulus controller owns measure / minimap drag / scroll sync / pickers.
//   • app.jsx App + ReactDOM.render → renderApp(bgId) exported at the bottom.
//   • blake-flock customRender React.createElement → h.
//   • content.jsx <image-slot> → a plain Stimulus drop-zone.
//
// eslint-disable -- ported third-party-style art code; kept faithful to source.

const STROKE = 2.6; // referenced by BlakeDragon; never defined in the prototype.
// visual.jsx — Magic Sheep visual primitives
// Paper backgrounds, washi tape, polaroids, stars, hearts, envelopes,
// squiggly underlines, hand-labels, sheep portrait badges, tab bar.
// Driven by CSS custom properties on .ms-root so day/night flip is just a class.

const MS_FONTS = {
  // Font-analysis mock-up: Amatic SC headings + Neucha body.
  homemade: '"Amatic SC", cursive',
  reenie:   '"Amatic SC", cursive',
  marker:   '"Amatic SC", cursive',
  kalam:    '"Amatic SC", cursive',
  shadows:  '"Amatic SC", cursive',
  indie:    '"Neucha", system-ui, sans-serif',
  gloria:   '"Amatic SC", cursive',
  caveat:   '"Amatic SC", cursive',
  patrick:  '"Amatic SC", cursive',
  // Subdued
  serif: '"Newsreader", "Lora", Georgia, serif',
  body: '"Neucha", system-ui, sans-serif',
};

// Multi-color ink palette echoing the real letter pens
const MS_INKS = {
  red: '#C04432',
  orange: '#D8782A',
  yellow: '#C99313',
  green: '#5C8C3E',
  moss: '#74853A',
  teal: '#2E847A',
  blue: '#3370A8',
  purple: '#7B5BB8',
  pink: '#C95E84',
  coral: '#D67A6A',
};

// 5-point star — allowed simple shape, used as twinkles + decorative stickers
function StarSticker({ size = 22, color = MS_INKS.coral, rotate = 0, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ transform: `rotate(${rotate}deg)`, ...style }}
      aria-hidden="true"
    >
      <path
        d="M12 2 L14.6 9.3 L22.3 9.6 L16.2 14.4 L18.4 21.8 L12 17.5 L5.6 21.8 L7.8 14.4 L1.7 9.6 L9.4 9.3 Z"
        fill={color}
        stroke="rgba(0,0,0,0.18)"
        strokeWidth="0.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Heart — simple iconic shape, used as accent
function HeartSticker({ size = 18, color = MS_INKS.pink, rotate = 0, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ transform: `rotate(${rotate}deg)`, ...style }}
      aria-hidden="true"
    >
      <path
        d="M12 20.5 C 12 20.5 4 14 4 8.6 C 4 5.6 6.4 3.2 9.4 3.2 C 10.8 3.2 12 4.1 12 4.1 C 12 4.1 13.2 3.2 14.6 3.2 C 17.6 3.2 20 5.6 20 8.6 C 20 14 12 20.5 12 20.5 Z"
        fill={color}
        stroke="rgba(0,0,0,0.18)"
        strokeWidth="0.6"
      />
    </svg>
  );
}

// Squiggle underline — a single sine-ish path
function Squiggle({ width = 120, color = MS_INKS.teal, thickness = 2.2, style }) {
  return (
    <svg width={width} height="10" viewBox="0 0 120 10" preserveAspectRatio="none" style={style} aria-hidden="true">
      <path
        d="M 2 6 Q 12 1, 22 6 T 42 6 T 62 6 T 82 6 T 102 6 T 118 6"
        stroke={color}
        strokeWidth={thickness}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// Paper background — warm cream with subtle fibers via SVG fractalNoise overlay
function PaperBg({ children, tone = 'cream', style }) {
  const bg =
    tone === 'kraft'
      ? 'linear-gradient(180deg, #E9D6B0 0%, #DEC79B 100%)'
      : tone === 'night'
        ? 'linear-gradient(180deg, #1B2444 0%, #14193A 100%)'
        : 'linear-gradient(180deg, #F6ECD4 0%, #EFE2C2 100%)';
  return (
    <div style={{ position: 'relative', background: bg, width: '100%', height: '100%', overflow: 'hidden', ...style }}>
      {/* paper fibers */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0, opacity: tone === 'night' ? 0.18 : 0.28, mixBlendMode: 'multiply', pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <filter id={`ms-noise-${tone}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="3" />
          <feColorMatrix values="0 0 0 0 0.35  0 0 0 0 0.28  0 0 0 0 0.16  0 0 0 0.18 0" />
        </filter>
        <rect width="100%" height="100%" filter={`url(#ms-noise-${tone})`} />
      </svg>
      {/* faint horizontal rule lines like a notebook */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: tone === 'night'
            ? 'repeating-linear-gradient(180deg, transparent 0 31px, rgba(255,233,165,0.05) 31px 32px)'
            : 'repeating-linear-gradient(180deg, transparent 0 31px, rgba(95,75,40,0.06) 31px 32px)',
        }}
      />
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>{children}</div>
    </div>
  );
}

// Washi tape — semi-transparent skewed strip
function WashiTape({ width = 110, color = MS_INKS.teal, pattern = 'stripe', rotate = -4, style }) {
  const patterns = {
    stripe: `repeating-linear-gradient(135deg, ${color}aa 0 6px, ${color}66 6px 12px)`,
    dots: `radial-gradient(${color}aa 1.6px, transparent 2.4px) 0 0 / 9px 9px, ${color}55`,
    solid: `${color}99`,
    check: `repeating-linear-gradient(0deg, ${color}aa 0 4px, transparent 4px 8px), repeating-linear-gradient(90deg, ${color}aa 0 4px, transparent 4px 8px), ${color}33`,
  };
  return (
    <div
      style={{
        width, height: 26,
        background: patterns[pattern] || patterns.stripe,
        transform: `rotate(${rotate}deg)`,
        boxShadow: '0 1px 3px rgba(40,30,12,0.18)',
        // torn ends
        clipPath: 'polygon(2% 18%, 5% 4%, 9% 22%, 14% 6%, 19% 18%, 25% 8%, 100% 8%, 100% 92%, 25% 92%, 19% 82%, 14% 94%, 9% 78%, 5% 96%, 2% 82%)',
        ...style,
      }}
    />
  );
}

// Polaroid — photo on white card, optional caption, slight rotation
function Polaroid({ src, alt = '', caption, rotate = -2, width = 150, captionFont = MS_FONTS.caveat, captionColor, style, children }) {
  return (
    <div
      style={{
        background: '#FBF6E8',
        padding: 8,
        paddingBottom: caption ? 28 : 10,
        boxShadow: '0 4px 14px rgba(40,30,12,0.22), 0 1px 0 rgba(255,255,255,0.6) inset',
        transform: `rotate(${rotate}deg)`,
        width,
        position: 'relative',
        ...style,
      }}
    >
      <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', background: '#222', position: 'relative' }}>
        {src ? (
          <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          children
        )}
      </div>
      {caption && (
        <div
          style={{
            position: 'absolute',
            left: 8, right: 8, bottom: 4,
            fontFamily: captionFont,
            fontSize: 17, lineHeight: 1,
            color: captionColor || MS_INKS.blue,
            textAlign: 'center',
          }}
        >
          {caption}
        </div>
      )}
    </div>
  );
}

// Hand-written label chip with optional star
function HandLabel({ children, color = MS_INKS.blue, star = false, size = 18, font = MS_FONTS.caveat, style }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: font, fontSize: size, color, lineHeight: 1.1,
        ...style,
      }}
    >
      {star && <StarSticker size={14} color={color} />}
      {children}
    </span>
  );
}

// Envelope (closed) — colored paper with wax seal
// Layout: small triangular flap occupies the TOP ~30% of height; the address
// area + stamp sit in the lower 70%. The wax seal lands at the flap tip,
// straddling the flap and the body (so it visually "closes" the flap onto the
// front of the envelope).
function Envelope({ width = 280, height = 170, color = '#F0E2BE', sealColor = MS_INKS.red, sender, subject, date, isNew, font = MS_FONTS.caveat, accent = MS_INKS.blue, style, onClick, tilt = 0 }) {
  const flapH = Math.round(height * 0.32);
  const addrTop = flapH + 8;
  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative', width, height,
        cursor: onClick ? 'pointer' : 'default',
        transform: `rotate(${tilt}deg)`,
        filter: 'drop-shadow(0 6px 14px rgba(40,30,12,0.22))',
        ...style,
      }}
    >
      {/* Envelope body */}
      <div
        style={{
          position: 'absolute', inset: 0,
          background: color,
          borderRadius: 4,
          boxShadow: 'inset 0 0 0 1px rgba(40,30,12,0.12)',
        }}
      />
      {/* Flap (triangle, top third — closed envelope) */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, top: 0, height: flapH,
          background: `linear-gradient(180deg, ${shade(color, 6)} 0%, ${shade(color, -10)} 100%)`,
          clipPath: `polygon(0 0, 100% 0, 50% 100%)`,
          boxShadow: 'inset 0 -1px 0 rgba(40,30,12,0.18)',
        }}
      />
      {/* Subtle horizontal seam where flap meets body */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: flapH,
        height: 1, background: 'rgba(40,30,12,0.10)',
      }} />
      {/* Address area — sits below the flap */}
      <div style={{
        position: 'absolute', left: 18, top: addrTop, right: 64,
        fontFamily: font, color: MS_INKS.blue,
      }}>
        <div style={{ fontSize: 11, color: '#7A6C50', letterSpacing: 1.2, textTransform: 'uppercase', fontFamily: MS_FONTS.body, fontWeight: 700 }}>
          From {sender || 'the Flock'}
        </div>
        <div style={{ fontSize: 22, lineHeight: 1.1, color: accent, marginTop: 2 }}>
          {subject || 'A baaaa-ulletin'}
        </div>
        {date && (
          <div style={{ fontSize: 12, color: '#9A8870', marginTop: 4, fontFamily: MS_FONTS.body, letterSpacing: 0.2 }}>
            {date}
          </div>
        )}
      </div>
      {/* Faux postage square — bottom right, out from under the flap */}
      <div style={{
        position: 'absolute', right: 12, bottom: 12,
        width: 38, height: 44,
        background: '#fff',
        border: `2px dashed #6F634F`,
        opacity: 0.65,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MS_FONTS.caveat, fontSize: 22, color: MS_INKS.red,
        transform: 'rotate(3deg)',
      }}>★</div>
      {/* Wax seal — at flap tip */}
      <div
        style={{
          position: 'absolute', left: '50%', top: flapH - 14,
          transform: 'translateX(-50%)',
          width: 28, height: 28, borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, ${shade(sealColor, 25)} 0%, ${sealColor} 60%, ${shade(sealColor, -30)} 100%)`,
          boxShadow: '0 2px 4px rgba(40,30,12,0.4), inset 0 -2px 3px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: MS_FONTS.caveat, fontSize: 20, color: '#fff',
        }}
      >
        ✿
      </div>
      {/* NEW badge */}
      {isNew && (
        <div style={{
          position: 'absolute', right: -10, top: -10,
          background: MS_INKS.yellow,
          color: MS_INKS.ink || '#2A2218',
          fontFamily: MS_FONTS.caveat, fontSize: 18,
          padding: '2px 10px', borderRadius: 999,
          boxShadow: '0 2px 6px rgba(40,30,12,0.25)',
          transform: 'rotate(8deg)',
          animation: 'msWiggle 2.6s ease-in-out infinite',
        }}>
          new! ✦
        </div>
      )}
    </div>
  );
}

// Sheep portrait badge — round photo with stitched border + name banner
function SheepBadge({ src, name, role, color = MS_INKS.teal, size = 96 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          width: size, height: size, borderRadius: '50%',
          background: '#FBF6E8',
          padding: 4,
          boxShadow: `0 0 0 2px ${color}, 0 0 0 2px ${color} inset, 0 6px 14px rgba(40,30,12,0.18)`,
          outline: `2px dashed ${color}`,
          outlineOffset: -8,
        }}
      >
        <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden' }}>
          <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      </div>
      <div style={{ fontFamily: MS_FONTS.caveat, fontSize: 22, color, lineHeight: 1 }}>{name}</div>
      <div style={{ fontFamily: MS_FONTS.body, fontSize: 11, color: '#6F634F', letterSpacing: 1.2, textTransform: 'uppercase' }}>{role}</div>
    </div>
  );
}

// Bottom tab bar — handmade nav, 5 sections
function TabBar({ active = 'home', onChange, night = false }) {
  const tabs = [
    { id: 'home', label: 'Home', mark: '✿' },
    { id: 'flock', label: 'Flock', mark: '✦' },
    { id: 'mail', label: 'Mail', mark: '✉' },
    { id: 'meadow', label: 'Meadow', mark: '✿' },
    { id: 'music', label: 'Music', mark: '♪' },
  ];
  return (
    <div
      style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        paddingBottom: 22, paddingTop: 8,
        background: night
          ? 'linear-gradient(180deg, rgba(20,25,58,0) 0%, rgba(20,25,58,0.92) 30%)'
          : 'linear-gradient(180deg, rgba(246,236,212,0) 0%, rgba(246,236,212,0.92) 30%)',
        display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end',
        zIndex: 4,
      }}
    >
      {tabs.map((t) => {
        const isActive = t.id === active;
        const color = isActive ? MS_INKS.coral : (night ? '#C9B98B' : '#8A7B5C');
        return (
          <button
            key={t.id}
            onClick={() => onChange && onChange(t.id)}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '4px 10px',
              fontFamily: MS_FONTS.caveat,
              fontSize: 18, color,
              position: 'relative',
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1, marginBottom: -2 }}>{t.mark}</span>
            <span style={{ lineHeight: 1 }}>{t.label}</span>
            {isActive && (
              <div style={{ position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)' }}>
                <Squiggle width={36} color={color} thickness={2} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Small helper: shade a hex by percent
function shade(hex, pct) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  let r = (num >> 16) & 0xff, g = (num >> 8) & 0xff, b = num & 0xff;
  const adj = (c) => Math.max(0, Math.min(255, Math.round(c + (255 - c) * (pct / 100))));
  const sub = (c) => Math.max(0, Math.min(255, Math.round(c + c * (pct / 100))));
  r = pct >= 0 ? adj(r) : sub(r);
  g = pct >= 0 ? adj(g) : sub(g);
  b = pct >= 0 ? adj(b) : sub(b);
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

// Tiny twinkling stars layer (decorative; CSS-driven, no JS animation)
function TwinkleField({ count = 8, color = MS_INKS.yellow, area = { w: 360, h: 720 }, seed = 0 }) {
  // deterministic positions from seed
  const items = [];
  for (let i = 0; i < count; i++) {
    const x = ((Math.sin(i * 12.9898 + seed) * 43758.5453) % 1 + 1) % 1;
    const y = ((Math.cos(i * 78.233 + seed) * 12345.678) % 1 + 1) % 1;
    const s = 8 + ((i * 7 + seed) % 10);
    const r = ((i * 37 + seed) % 60) - 30;
    const d = ((i * 0.7) % 3).toFixed(2);
    items.push(
      <div
        key={i}
        style={{
          position: 'absolute',
          left: `${(x * area.w).toFixed(1)}px`,
          top: `${(y * area.h).toFixed(1)}px`,
          animation: `msTwinkle 3.8s ease-in-out ${d}s infinite`,
          pointerEvents: 'none',
        }}
      >
        <StarSticker size={s} color={color} rotate={r} />
      </div>
    );
  }
  return <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>{items}</div>;
}

// Hand-drawn section header — squiggle underline + handwriting
function HandHeading({ children, color = MS_INKS.ink || '#2A2218', size = 36, font, underlineColor = MS_INKS.coral, style, wobble = true }) {
  // Children might be a raw string OR an __OmT-wrapped React element. Wobble
  // extracts the text either way.
  const content = wobble ? <Wobble amount={1.2}>{children}</Wobble> : children;
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, ...style }}>
      <span
        style={{
          fontFamily: font || MS_FONTS.homemade,
          fontSize: size, fontWeight: 400,
          color, lineHeight: 1, letterSpacing: 0.2,
        }}
      >
        {content}
      </span>
      <Squiggle width={Math.min(220, size * 4)} color={underlineColor} thickness={2.5} style={{ marginLeft: 4 }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Wobble — wrap text to introduce per-word (or per-char) micro-rotations,
// vertical jiggle, and slight ink-saturation variance. Makes any handwriting
// font look like it was actually drawn, not typeset.
//
// Children may be a raw string OR a React element (the host wraps text nodes
// in an __OmT tracker for direct-edit). We recursively extract the text.
// ─────────────────────────────────────────────────────────────
function _hash(str, seed) {
  let h = seed | 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 2654435761);
  }
  return (h >>> 0) / 0xffffffff;
}

function _extractText(node) {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(_extractText).join('');
  // React element — pull children out and recurse
  if (node && typeof node === 'object' && 'props' in node) {
    return _extractText(node.props.children);
  }
  return '';
}

function Wobble({ children, amount = 1, mode = 'word', seed = 7, inkVary = true, baseline = true }) {
  const str = _extractText(children);
  if (!str) return null;
  // We need to preserve spaces. For 'word' mode, split keeping spaces.
  const tokens = mode === 'char'
    ? Array.from(str).map((c, i) => ({ t: c, isSpace: c === ' ', k: i }))
    : str.split(/(\s+)/).map((t, i) => ({ t, isSpace: /^\s+$/.test(t), k: i }));

  return (
    <span style={{ display: 'inline' }}>
      {tokens.map(({ t, isSpace, k }) => {
        if (isSpace) return <span key={k}>{t}</span>;
        const r1 = _hash(t + ':r:' + k, seed);
        const r2 = _hash(t + ':y:' + k, seed + 31);
        const r3 = _hash(t + ':o:' + k, seed + 91);
        const rot = (r1 - 0.5) * 4 * amount;       // -2..+2 deg × amount
        const dy = baseline ? (r2 - 0.5) * 2.2 * amount : 0;
        const op = inkVary ? 0.85 + r3 * 0.15 : 1; // 0.85..1
        return (
          <span
            key={k}
            style={{
              display: 'inline-block',
              transform: `translateY(${dy.toFixed(2)}px) rotate(${rot.toFixed(2)}deg)`,
              opacity: op,
            }}
          >
            {t}
          </span>
        );
      })}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// MixedHand — rotate handwriting fonts per character (or per word).
// Deterministic per (text, position, seed). Pulls from MS_HAND_PENS.
// Use for: letter body, signatures, headlines that should look written.
// ─────────────────────────────────────────────────────────────
const MS_HAND_PENS = [
  '"Caveat", cursive',
  '"Homemade Apple", cursive',
  '"Reenie Beanie", cursive',
  '"Kalam", cursive',
  '"Indie Flower", cursive',
  '"Patrick Hand", cursive',
  '"Gloria Hallelujah", cursive',
  '"Just Another Hand", cursive',
  '"Architects Daughter", cursive',
  '"Shadows Into Light", cursive',
  '"Nothing You Could Do", cursive',
  '"Cedarville Cursive", cursive',
];

const MS_PEN_TUNE = {
  '"Caveat", cursive':              { sz: 1.00, dy: 0,    weight: 500 },
  '"Homemade Apple", cursive':      { sz: 0.74, dy: -1.5, weight: 400 },
  '"Reenie Beanie", cursive':       { sz: 1.10, dy: 0,    weight: 400 },
  '"Kalam", cursive':               { sz: 0.92, dy: 0,    weight: 400 },
  '"Indie Flower", cursive':        { sz: 0.94, dy: 0,    weight: 400 },
  '"Patrick Hand", cursive':        { sz: 0.94, dy: -1,   weight: 400 },
  '"Gloria Hallelujah", cursive':   { sz: 0.86, dy: -1,   weight: 400 },
  '"Just Another Hand", cursive':   { sz: 1.18, dy: 4,    weight: 400 },
  '"Architects Daughter", cursive': { sz: 0.84, dy: -0.5, weight: 400 },
  '"Shadows Into Light", cursive':  { sz: 1.00, dy: 0,    weight: 400 },
  '"Nothing You Could Do", cursive':{ sz: 0.94, dy: 0,    weight: 400 },
  '"Cedarville Cursive", cursive':  { sz: 0.98, dy: 0,    weight: 400 },
};

function MixedHand({
  children, size = 22, color = '#2A2218',
  rotate = 1.5, baseline = 1.5,
  pens = MS_HAND_PENS,
  seed = 0,
  mode = 'char',          // 'char' | 'word'
  smoothWalk = false,
  style,
}) {
  const str = _extractText(children);
  if (!str) return null;
  const units = [];
  if (mode === 'word') {
    str.split(/(\s+)/).forEach((t, i) => {
      units.push({ t, space: /^\s+$/.test(t), k: 'w' + i });
    });
  } else {
    Array.from(str).forEach((c, i) => {
      units.push({ t: c, space: c === ' ', k: 'c' + i });
    });
  }
  let walk = Math.floor(_hash('walk' + seed, seed) * pens.length);
  return (
    <span style={{ display: 'inline', color, ...style }}>
      {units.map((u) => {
        if (u.space) return <span key={u.k}>{u.t}</span>;
        const r1 = _hash(u.t + ':' + u.k, seed);
        const r2 = _hash(u.t + ':r:' + u.k, seed + 17);
        const r3 = _hash(u.t + ':y:' + u.k, seed + 41);
        const r4 = _hash(u.t + ':o:' + u.k, seed + 67);
        let penIdx;
        if (smoothWalk) {
          walk = (walk + Math.round((r1 - 0.5) * 4) + pens.length) % pens.length;
          penIdx = walk;
        } else {
          penIdx = Math.floor(r1 * pens.length);
        }
        const pen = pens[penIdx];
        const tune = MS_PEN_TUNE[pen] || { sz: 1, dy: 0, weight: 400 };
        const rot = (r2 - 0.5) * 2 * rotate;
        const dy = (r3 - 0.5) * 2 * baseline + tune.dy;
        const op = 0.86 + r4 * 0.14;
        return (
          <span
            key={u.k}
            style={{
              fontFamily: pen,
              fontWeight: tune.weight,
              fontSize: `${(size * tune.sz).toFixed(2)}px`,
              display: 'inline-block',
              transform: `translateY(${dy.toFixed(2)}px) rotate(${rot.toFixed(2)}deg)`,
              opacity: op,
              lineHeight: 1,
            }}
          >
            {u.t}
          </span>
        );
      })}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Scribble — irregular hand-drawn rectangle/oval outline using SVG path.
// Use this in place of a plain CSS border when you want a "drawn around"
// look. Width/height auto from children via wrapper.
// ─────────────────────────────────────────────────────────────
function Scribble({ width, height, color = MS_INKS.coral, strokeWidth = 2, kind = 'rect', seed = 3, style }) {
  const w = width, h = height;
  // Generate a wobbly rectangle path
  const jitter = (s) => (_hash('j' + s, seed) - 0.5) * 4;
  let path;
  if (kind === 'oval') {
    // 4 anchor points around an ellipse with jitter
    const cx = w / 2, cy = h / 2;
    const rx = w / 2 - 4, ry = h / 2 - 4;
    path = `M ${cx} ${4 + jitter('t')}
            C ${cx + rx * 0.85 + jitter('a')} ${4 + jitter('b')}, ${cx + rx + jitter('c')} ${cy - ry * 0.4 + jitter('d')}, ${cx + rx + jitter('e')} ${cy + jitter('f')}
            C ${cx + rx + jitter('g')} ${cy + ry * 0.55 + jitter('h')}, ${cx + rx * 0.6 + jitter('i')} ${h - 4 + jitter('j')}, ${cx + jitter('k')} ${h - 4 + jitter('l')}
            C ${cx - rx * 0.6 + jitter('m')} ${h - 4 + jitter('n')}, ${cx - rx + jitter('o')} ${cy + ry * 0.55 + jitter('p')}, ${cx - rx + jitter('q')} ${cy + jitter('r')}
            C ${cx - rx + jitter('s')} ${cy - ry * 0.4 + jitter('t1')}, ${cx - rx * 0.85 + jitter('u')} ${4 + jitter('v')}, ${cx + jitter('w')} ${4 + jitter('x')} Z`;
  } else {
    // rectangle with wobbly sides
    const segs = 5;
    const xs = (frac) => 6 + (w - 12) * frac + jitter('x' + frac);
    const ys = (frac) => 6 + (h - 12) * frac + jitter('y' + frac);
    let d = `M ${xs(0)} ${ys(0)} `;
    for (let i = 1; i <= segs; i++) d += `L ${xs(i / segs)} ${ys(0)} `;
    for (let i = 1; i <= segs; i++) d += `L ${xs(1)} ${ys(i / segs)} `;
    for (let i = 1; i <= segs; i++) d += `L ${xs(1 - i / segs)} ${ys(1)} `;
    for (let i = 1; i <= segs; i++) d += `L ${xs(0)} ${ys(1 - i / segs)} `;
    d += 'Z';
    path = d;
  }
  return (
    <svg
      width={w} height={h} viewBox={`0 0 ${w} ${h}`}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', ...style }}
      aria-hidden="true"
    >
      <path d={path} stroke={color} strokeWidth={strokeWidth} fill="none" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Doodle arrow — hand-drawn arrow pointing wherever
// ─────────────────────────────────────────────────────────────
function DoodleArrow({ width = 90, height = 60, color = MS_INKS.coral, strokeWidth = 2, flip = false, style }) {
  return (
    <svg width={width} height={height} viewBox="0 0 90 60" style={{ transform: flip ? 'scaleX(-1)' : 'none', ...style }} aria-hidden="true">
      <path
        d="M 4 12 C 18 6, 36 4, 52 14 C 66 22, 70 36, 72 48"
        stroke={color} strokeWidth={strokeWidth} fill="none" strokeLinecap="round"
      />
      <path
        d="M 64 40 L 73 52 L 80 38"
        stroke={color} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

Object.assign(window, {
  MS_FONTS, MS_INKS,
  StarSticker, HeartSticker, Squiggle, PaperBg, WashiTape, Polaroid,
  HandLabel, Envelope, SheepBadge, TabBar, TwinkleField, HandHeading,
  Wobble, MixedHand, Scribble, DoodleArrow,
  msShade: shade,
});
// blake-flock.jsx — the flock built STRICTLY on the original drawing.
// Derek IS the original, untouched. Every other sheep reuses the exact same
// washes + scratchy outline + legs + face, only (a) stretched in proportion
// (line weight stays constant via non-scaling-stroke), (b) given a mood palette,
// (c) given one small prop. So they always read as the same hand.

const INK = '#2B2622';
const FACE = '#F0DBB6';
const RBOW = ['#E0574B', '#E8913C', '#EBC43F', '#5BA85A', '#3E91C7', '#7B6FC4', '#C96BA4'];
const MOODS = {
  rainbow: RBOW,
  wistful: ['#9AA0A6', '#A8AEB3', '#B7BCC0', '#969CA1', '#AEB4B8', '#8C9297', '#A2A8AD'],
  fiery:   ['#D8392B', '#E5552A', '#EFA13A', '#D42E1E', '#E8632B', '#EF8A2A', '#D8392B'],
  calm:    ['#3E7FA8', '#5BA8B8', '#6FA9C4', '#7B86C4', '#5E89A8', '#8FA9C9', '#4E8C9A'],
  dream:   ['#7B6FC4', '#9A7FC4', '#C96BA4', '#8E86C9', '#B07FC0', '#6F8FC4', '#A98FD0'],
  warm:    ['#E0574B', '#E8913C', '#EBC43F', '#E0894B', '#D9794B', '#EBA84B', '#E0574B'],
};
function rr(s) { const x = Math.sin(s * 91.7) * 43758.5; return x - Math.floor(x); }

// original face anchor (from the reference drawing)
const FCX = 122, FCY = 120, FRX = 22, FRY = 19;

function BlakeSheep({ size = 220, p = {} }) {
  const cx = 110, cy = 104;
  const sx = p.sx ?? 1, sy = p.sy ?? 1;
  const cols = MOODS[p.mood || 'rainbow'] || RBOW;
  const acc = p.accessories || [];
  const has = (a) => acc.includes(a);
  const T = `translate(110 110) scale(${sx} ${sy}) translate(-110 -110)`;
  const ns = { vectorEffect: 'non-scaling-stroke' };

  return (
    <svg width={size} height={size} viewBox="0 0 220 220" style={{ transform: `rotate(${p.tilt || 0}deg)`, display: 'block' }}>
      {/* loose watercolour washes, offset from the line — EXACTLY as the original (blur 2px, opacity .7) */}
      <g transform={T}>
        <g style={{ filter: 'blur(2px)' }} opacity="0.7">
          {cols.map((c, i) => (
            <ellipse key={i} cx={cx - 46 + i * 16 + (rr(i) - 0.5) * 10} cy={cy + (rr(i + 3) - 0.5) * 26} rx="30" ry="34" fill={c} />
          ))}
        </g>

        <g fill="none" stroke={INK} strokeLinecap="round" strokeLinejoin="round">
          {/* horns / tuft behind the body line */}
          {has('horns') && <g><path d="M104 96 q-20 -6 -16 -28 q3 -14 18 -8" strokeWidth="5" {...ns} /><path d="M140 96 q20 -6 16 -28 q-3 -14 -18 -8" strokeWidth="5" {...ns} /></g>}
          {has('tuft') && <path d="M100 50 q8 -16 16 -2 q8 -12 14 2" strokeWidth="2.4" {...ns} />}

          {/* scratchy double ink outline — the original body */}
          <path d="M58 118 C40 112 40 78 64 74 C58 54 92 44 100 62 C112 44 150 52 146 76 C170 78 168 116 146 120 C156 140 130 156 112 146 C108 166 74 164 70 144 C50 150 48 126 58 118Z" strokeWidth="2.6" {...ns} />
          <path d="M60 120 C44 113 45 80 66 76 M100 62 C112 46 148 54 145 78" strokeWidth="1.2" opacity="0.6" {...ns} />
          {/* spindly legs */}
          <path d="M84 150 l-6 26 M104 156 l-2 24 M122 154 l4 26 M140 150 l8 24" strokeWidth="2.4" {...ns} />

          {/* face — original */}
          <ellipse cx={FCX} cy={FCY} rx={FRX} ry={FRY} fill={FACE} stroke={INK} strokeWidth="2.2" {...ns} />
          {/* whiskers — original */}
          <path d="M104 112 l-10 -4 M104 122 l-11 2" strokeWidth="2" {...ns} />
          {/* eyes + smile (expression) */}
          <BlakeFace kind={p.eyes || 'happy'} ns={ns} />

          {/* small props (tracked in the same transformed group) */}
          {has('glasses') && <g strokeWidth="2"><circle cx="116" cy="118" r="6.5" {...ns} /><circle cx="130" cy="118" r="6.5" {...ns} /><line x1="122.5" y1="118" x2="123.5" y2="118" {...ns} /></g>}
          {has('monocle') && <g strokeWidth="2"><circle cx="130" cy="118" r="7" {...ns} /><path d="M130 125 l3 12" {...ns} /></g>}
          {has('goggles') && <g><rect x="104" y="110" width="36" height="15" rx="6" fill="#3E91C7" stroke={INK} strokeWidth="2.4" opacity="0.92" /><line x1="100" y1="116" x2="104" y2="116" stroke={INK} strokeWidth="2.4" /><line x1="140" y1="116" x2="146" y2="116" stroke={INK} strokeWidth="2.4" /></g>}
          {has('snorkel') && <g><ellipse cx="122" cy="118" rx="20" ry="14" fill="#BFE0F2" stroke={INK} strokeWidth="2.4" opacity="0.9" /><path d="M142 110 q10 -2 10 -16" fill="none" stroke={INK} strokeWidth="2.4" /><rect x="148" y="90" width="6" height="6" fill="#E0574B" stroke={INK} strokeWidth="1.6" /></g>}
          {has('beard') && <g strokeWidth="2">{[-9, -3, 3, 9].map((d, i) => <path key={i} d={`M${122 + d} 137 q-2 12 1 18`} {...ns} />)}</g>}
        </g>

        {/* filled props */}
        {has('nursecap') && <g><path d="M107 100 q15 -10 30 0 l0 7 q-15 -7 -30 0 Z" fill="#fff" stroke={INK} strokeWidth="2" {...ns} /><path d="M122 101 v7 M118.5 104.5 h7" stroke="#D8392B" strokeWidth="2.2" strokeLinecap="round" {...ns} /></g>}
        {has('hardhat') && <g fill="#EBC43F" stroke={INK} strokeWidth="2"><path d="M105 104 a17 17 0 0 1 34 0 Z" {...ns} /><line x1="101" y1="104" x2="143" y2="104" strokeLinecap="round" {...ns} /><line x1="122" y1="90" x2="122" y2="104" {...ns} /></g>}
        {has('flatcap') && <path d="M101 104 q21 -16 38 -3 q5 3 -3 7 l-35 0 Z" fill="#6E7B86" stroke={INK} strokeWidth="2" strokeLinejoin="round" {...ns} />}
        {has('tophat') && <g fill="#3A352E" stroke={INK} strokeWidth="2"><rect x="111" y="80" width="22" height="20" {...ns} /><line x1="103" y1="100" x2="141" y2="100" strokeLinecap="round" {...ns} /></g>}
        {has('hairbow') && <g fill="#C96BA4" stroke={INK} strokeWidth="1.8"><path d="M142 104 l-9 -6 v12 Z" {...ns} /><path d="M142 104 l9 -6 v12 Z" {...ns} /><circle cx="142" cy="104" r="3" {...ns} /></g>}
        {has('apron') && <path d="M110 150 h24 l4 22 h-32 Z" fill="#E8A9C0" stroke={INK} strokeWidth="1.8" strokeLinejoin="round" {...ns} />}
        {has('scarf') && <path d="M100 138 q22 12 44 0 l0 9 q-22 12 -44 0 Z" fill="#3E91C7" stroke={INK} strokeWidth="1.8" {...ns} />}
        {has('violin') && <g><path d="M150 138 q12 -5 19 5 q7 11 -3 18 q-14 7 -19 -5 q-3 -12 3 -18 Z" fill="#A0522D" stroke={INK} strokeWidth="1.8" {...ns} /><line x1="151" y1="136" x2="170" y2="118" stroke={INK} strokeWidth="1.6" strokeLinecap="round" {...ns} /></g>}
      </g>
    </svg>
  );
}

function BlakeFace({ kind, ns }) {
  if (kind === 'closed') return <g strokeWidth="2"><path d="M112 118 q6 6 12 0 M126 118 q6 6 12 0" {...ns} /><path d="M118 130 q5 5 10 0" strokeWidth="1.8" {...ns} /></g>;
  if (kind === 'tired') return <g strokeWidth="2"><path d="M112 119 q6 4 12 0 M126 119 q6 4 12 0" {...ns} /><path d="M118 131 q5 4 10 0" strokeWidth="1.8" {...ns} /></g>;
  if (kind === 'wide') return <g><circle cx="118" cy="118" r="4" fill="#fff" stroke={INK} strokeWidth="1.4" {...ns} /><circle cx="130" cy="118" r="4" fill="#fff" stroke={INK} strokeWidth="1.4" {...ns} /><circle cx="118" cy="118" r="1.8" fill={INK} /><circle cx="130" cy="118" r="1.8" fill={INK} /><ellipse cx="124" cy="131" rx="2.4" ry="3" fill={INK} /></g>;
  if (kind === 'smirk') return <g><circle cx="118" cy="118" r="2.3" fill={INK} /><path d="M124 118 q6 -5 12 0" fill="none" stroke={INK} strokeWidth="2" {...ns} /><path d="M116 131 q8 6 16 -1" fill="none" stroke={INK} strokeWidth="1.8" {...ns} /></g>;
  // happy — the original
  return <g><circle cx="118" cy="118" r="2.3" fill={INK} /><circle cx="130" cy="118" r="2.3" fill={INK} /><path d="M118 130 q5 5 10 0" fill="none" stroke={INK} strokeWidth="1.8" {...ns} /></g>;
}

// Derek = the original, exactly (sx=1, sy=1, rainbow, happy)
const DEREK = { sx: 1, sy: 1, mood: 'rainbow', eyes: 'happy' };

// ── Ember — the Magic Dragon, in the same wash + crisp-ink language ──
// Rainbow wash through the middle, big rounded body, little stick legs, wings,
// and a stream of ghostly low-opacity blurred flame circles.
function BlakeDragon({ size = 240, p = {} }) {
  const cols = MOODS[p.mood || 'rainbow'] || RBOW;
  const flame = ['#EBC43F', '#E8913C', '#E0574B', '#E8913C', '#EBC43F'];
  return (
    <svg width={size} height={size * 0.84} viewBox="0 0 260 218" style={{ display: 'block' }}>
      <defs>
        <filter id="dragon-wash" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="8" /></filter>
        <filter id="dragon-flame" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="5" /></filter>
      </defs>

      {/* rainbow wash through the middle of the body */}
      <g filter="url(#dragon-wash)" opacity="0.72">
        {cols.map((c, i) => <circle key={i} cx={60 + i * 18} cy={128 + (rr(i + 2) - 0.5) * 22} r="30" fill={c} opacity="0.6" />)}
      </g>

      {/* wings behind — soft wash + crisp ink, scalloped lower edge */}
      <g>
        <path d="M120 96 Q150 36 206 52 Q198 78 176 86 Q200 92 196 112 Q170 104 150 110 Q134 104 120 96 Z" fill="#C9B7E0" filter="url(#dragon-wash)" opacity="0.5" />
        <path d="M120 96 Q150 36 206 52 Q198 78 176 86 Q200 92 196 112 Q170 104 150 110 Q134 104 120 96 Z" fill="none" stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
        <path d="M150 60 Q156 80 150 108 M176 58 Q180 78 176 86" fill="none" stroke={INK} strokeWidth="1.6" opacity="0.55" />
      </g>

      {/* little stick legs */}
      <g stroke={INK} strokeWidth={STROKE} strokeLinecap="round">
        <path d="M70 168 l-6 30 M96 176 l-2 28 M150 176 l6 28 M176 168 l10 28" />
      </g>

      {/* tail, curling left with a spade tip */}
      <path d="M44 140 Q4 150 14 186 Q18 198 30 196" fill="none" stroke={INK} strokeWidth={STROKE} strokeLinecap="round" />
      <path d="M30 196 l-12 6 l10 8 l8 -6 Z" fill={RBOW[5]} stroke={INK} strokeWidth={STROKE * 0.7} strokeLinejoin="round" />

      {/* big rounded body */}
      <path d="M44 138 C40 96 96 86 120 100 C150 86 196 96 196 138 C196 180 150 192 120 184 C92 192 48 182 44 138 Z" fill="none" stroke={INK} strokeWidth={STROKE} strokeLinejoin="round" />
      {/* back spikes */}
      <path d="M86 92 l8 -16 8 16 M112 90 l8 -16 8 16 M138 92 l8 -16 8 16" fill="none" stroke={INK} strokeWidth={STROKE * 0.8} strokeLinejoin="round" />
      {/* tummy curl */}
      <path d="M70 150 q40 24 100 6" fill="none" stroke={INK} strokeWidth="1.4" opacity="0.5" />

      {/* neck + head (up-right) */}
      <path d="M180 130 C214 126 222 96 214 78" fill="none" stroke={INK} strokeWidth={STROKE} strokeLinecap="round" />
      <ellipse cx="216" cy="66" rx="26" ry="22" fill="#F0DBB6" stroke={INK} strokeWidth={STROKE} />
      {/* little horns */}
      <path d="M206 48 q-2 -14 -10 -18 M226 48 q2 -14 10 -18" fill="none" stroke={INK} strokeWidth={STROKE * 0.8} strokeLinecap="round" />
      {/* face */}
      <circle cx="210" cy="62" r="2.6" fill={INK} /><circle cx="224" cy="62" r="2.6" fill={INK} />
      <path d="M208 72 q6 5 12 1" fill="none" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
      {/* nostril + snout */}
      <circle cx="238" cy="70" r="1.6" fill={INK} />

      {/* ghostly flame breath — clusters of low-opacity blurred circles */}
      <g filter="url(#dragon-flame)">
        {Array.from({ length: 16 }).map((_, i) => {
          const t = i / 15, x = 244 + t * 70 + (rr(i) - 0.5) * 10, y = 74 + t * 26 + (rr(i + 4) - 0.5) * 22;
          const r = 12 - t * 7 + rr(i + 9) * 4;
          return <circle key={i} cx={x} cy={y} r={Math.max(3, r)} fill={flame[i % flame.length]} opacity={0.16 + (1 - t) * 0.28} />;
        })}
      </g>
    </svg>
  );
}

const BLAKE_FLOCK = [
  { id: 'derek', name: 'Derek', found: true, rarity: 5, no: 1, title: 'The Original', job: 'started it all', traits: ['the first', 'rainbow', 'sincere chaos'],
    blurb: 'The very first Magic Sheep — the one everyone hopes to spot. Tracked by sparkles, snacks, and several open questions. Voted Maybe.', p: { ...DEREK } },
  { id: 'whisp', name: 'Whisp', found: true, rarity: 3, no: 2, title: 'The Lookout', job: 'watches the sky', traits: ['tall', 'dreamy', 'gazes up'],
    blurb: 'Stands a little taller than the rest to watch for weather and wonders. Mostly wonders.', p: { sx: 0.82, sy: 1.12, mood: 'calm', eyes: 'up', tilt: -1, accessories: ['tuft'] } },
  { id: 'plume', name: 'Prof. Plume', found: true, rarity: 4, no: 3, title: 'The Inventor', job: 'engineer of Wool-Fi™', traits: ['monocled', 'precise', 'secretive'],
    blurb: 'Engineered the Wool-Fi. Refuses to explain it. Possibly powered entirely by gossip.', p: { sx: 1, sy: 1, mood: 'calm', eyes: 'happy', accessories: ['monocle'] } },
  { id: 'pansy', name: 'Nurse Pansy', found: true, rarity: 4, no: 4, title: 'The Carer', job: 'meadow nurse', traits: ['kind', 'calm', 'prepared'],
    blurb: 'Patches scraped knees and bruised feelings alike. Keeps a clean hankie for every emergency.', p: { sx: 1.02, sy: 1, mood: 'rainbow', eyes: 'closed', accessories: ['nursecap'] } },
  { id: 'fern', name: 'Fiddler Fern', found: true, rarity: 4, no: 5, title: 'The Musician', job: 'violinist', traits: ['dreamy', 'graceful', 'perfect pitch'],
    blurb: 'Plays the meadow to sleep each night. The lambs will not nap to anyone else.', p: { sx: 0.84, sy: 1.12, mood: 'dream', eyes: 'closed', accessories: ['violin', 'hairbow'] } },
  { id: 'percy', name: 'Percy Pipe', found: true, rarity: 3, no: 6, title: 'The Plumber', job: 'fixes the rain', traits: ['sturdy', 'cheery', 'whistles'],
    blurb: 'Keeps the meadow\u2019s streams running and the cloud-taps from dripping on a Tuesday.', p: { sx: 1.22, sy: 0.98, mood: 'warm', eyes: 'smirk', accessories: ['flatcap'] } },
  { id: 'builder-jack', name: 'Builder Jack', found: true, rarity: 3, no: 7, title: 'The Builder', job: 'raises barns', traits: ['strong', 'practical', 'tireless'],
    blurb: 'Built every fence Cinder has ever broken. Twice. Cheerfully.', p: { sx: 1.18, sy: 1.02, mood: 'warm', eyes: 'wide', accessories: ['hardhat'] } },
  { id: 'ada', name: 'Ada', found: true, rarity: 4, no: 8, title: 'The Programmer', job: 'codes the Wool-Fi', traits: ['clever', 'focused', 'tea-powered'],
    blurb: 'Writes the spells that keep the letters arriving. Has opinions about semicolons.', p: { sx: 0.95, sy: 1.05, mood: 'calm', eyes: 'happy', accessories: ['glasses'] } },
  { id: 'marigold', name: 'Marigold', found: true, rarity: 3, no: 9, title: 'The Homemaker', job: 'keeps the meadow cosy', traits: ['warm', 'generous', 'baking'],
    blurb: 'There is always room at her table and something rising in the oven. Smells of cinnamon.', p: { sx: 1.26, sy: 1.04, mood: 'warm', eyes: 'closed', accessories: ['apron'] } },
  { id: 'bluebell', name: 'Old Bluebell', found: true, rarity: 4, no: 10, title: 'The Wistful', job: 'remembers everything', traits: ['elderly', 'gentle', 'a little sad'],
    blurb: 'Misses friends who have drifted off like clouds. Tells the softest, longest stories.', p: { sx: 1.12, sy: 0.82, mood: 'wistful', eyes: 'tired', accessories: ['glasses'], tilt: 2 } },
  { id: 'cinder', name: 'Cinder', found: true, rarity: 5, no: 11, title: 'The Fiery', job: 'meadow firebrand', traits: ['bold', 'hot-headed', 'all heart'],
    blurb: 'Argues with the weather and usually wins. Horns mostly for show. Mostly.', p: { sx: 1.04, sy: 0.96, mood: 'fiery', eyes: 'smirk', accessories: ['horns'], tilt: 1 } },
  { id: 'tuft', name: 'Little Tuft', found: true, rarity: 2, no: 12, title: 'The Lamb', job: 'youngest of all', traits: ['tiny', 'curious', 'bouncy'],
    blurb: 'Newest to the meadow and into absolutely everything. Naps mid-sentence.', p: { sx: 0.72, sy: 0.7, mood: 'rainbow', eyes: 'wide', accessories: ['tuft'] } },
  { id: 'q1', name: null, found: false, rarity: 4, no: 13, p: { sx: 1, sy: 1, mood: 'dream', eyes: 'happy' } },
  { id: 'q2', name: null, found: false, rarity: 5, no: 14, p: { sx: 1.2, sy: 0.9, mood: 'warm', eyes: 'happy' } },
  { id: 'ember', name: 'Ember', found: true, rarity: 5, no: 15, title: 'The Magic Dragon', job: 'keeps the meadow warm', traits: ['rainbow', 'winged', 'gentle fire'],
    blurb: 'Breathes soft, ghostly rainbow fire that warms but never burns. Toasts marshmallows for the whole flock.', customRender: (pr) => h(window.BlakeDragon, pr) },
];

function BlakeCard({ sheep, font = '"Schoolbell", cursive' }) {
  const found = sheep.found, rare = sheep.rarity >= 5, A = '#3E91C7';
  return (
    <div style={{ width: 232, height: 360, borderRadius: 16, position: 'relative', overflow: 'hidden',
      background: found ? 'linear-gradient(160deg,#FBF6E8,#F2E7CC)' : 'linear-gradient(160deg,#ECE3CF,#DDD1B4)',
      border: `2.5px solid ${found ? A : '#B9A982'}`,
      boxShadow: rare ? `0 14px 30px rgba(40,30,12,0.26), 0 0 0 4px ${A}33` : '0 10px 22px rgba(40,30,12,0.2)',
      display: 'flex', flexDirection: 'column', fontFamily: '"Nunito", sans-serif' }}>
      {rare && found && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.45, background: 'linear-gradient(115deg,transparent 38%,rgba(255,255,255,0.55) 49%,rgba(180,220,255,0.4) 52%,transparent 64%)' }} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: found ? A : '#B9A982', color: '#FFF9EC' }}>
        <span style={{ fontFamily: font, fontSize: 18 }}>No. {String(sheep.no).padStart(2, '0')}</span>
        <span style={{ fontSize: 13, letterSpacing: 1 }}>{'★'.repeat(sheep.rarity)}<span style={{ opacity: 0.4 }}>{'★'.repeat(5 - sheep.rarity)}</span></span>
      </div>
      <div style={{ margin: '10px 12px 0', borderRadius: 12, height: 168, position: 'relative', overflow: 'hidden',
        background: found ? 'radial-gradient(circle at 50% 44%,#FFFDF6,#EFE3C6)' : 'radial-gradient(circle at 50% 44%,#C9BD9E,#A99B78)',
        boxShadow: 'inset 0 0 0 1.5px rgba(40,30,12,0.12)' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', filter: found ? 'none' : 'blur(7px) grayscale(0.4)' }}>
          <BlakeSheep size={172} p={sheep.p} />
        </div>
        {!found && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font, fontSize: 92, color: 'rgba(255,249,236,0.92)', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>?</div>}
      </div>
      <div style={{ padding: '8px 14px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontFamily: font, fontSize: 26, color: found ? '#2A2218' : '#8E815F', lineHeight: 1 }}>{found ? sheep.name : '? ? ?'}</div>
        <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: A, fontWeight: 800, marginTop: 3 }}>{found ? `${sheep.title} · ${sheep.job}` : 'not yet spotted'}</div>
        {found && <div style={{ fontFamily: font, fontSize: 14.5, color: '#5C4F3A', lineHeight: 1.32, marginTop: 6 }}>{sheep.blurb}</div>}
        <div style={{ marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 5, paddingTop: 8 }}>
          {(found ? sheep.traits : ['?', '?', '?']).map((t, i) => (
            <span key={i} style={{ fontSize: 11, fontWeight: 700, color: '#5C4F3A', background: found ? `${A}22` : 'rgba(0,0,0,0.06)', borderRadius: 999, padding: '2px 9px' }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlakeDefault({ size = 160 }) { return <BlakeSheep size={size} p={DEREK} />; }

Object.assign(window, { BlakeSheep, BlakeDragon, BLAKE_FLOCK, BlakeCard, BlakeDefault, DEREK, BLAKE_MOODS: MOODS, BLAKE_INK: INK });
// scene.jsx — the Blake-style world: an Alps hero scene + a kit of Blake-style
// decorative objects (sun, pine, christmas tree, cloud, snow, plane, skier,
// ski-lift) all in the same constant-weight ink + soft wash language.

const SINK = '#2B2622';

// ── small Blake-style objects (constant ink weight + a soft wash) ──
function BSun({ s = 1 }) {
  return (
    <svg width={70 * s} height={70 * s} viewBox="0 0 70 70" aria-hidden="true">
      <defs><filter id="bsun-b"><feGaussianBlur stdDeviation="5" /></filter></defs>
      <circle cx="35" cy="35" r="24" fill="#F4C84A" filter="url(#bsun-b)" opacity="0.6" />
      {Array.from({ length: 12 }).map((_, i) => { const a = (i / 12) * Math.PI * 2; return <line key={i} x1={35 + Math.cos(a) * 21} y1={35 + Math.sin(a) * 21} x2={35 + Math.cos(a) * 30} y2={35 + Math.sin(a) * 30} stroke={SINK} strokeWidth="2.2" strokeLinecap="round" />; })}
      <circle cx="35" cy="35" r="17" fill="#F6D45F" stroke={SINK} strokeWidth="2.6" />
    </svg>
  );
}
function BPine({ s = 1, snow = false }) {
  return (
    <svg width={54 * s} height={72 * s} viewBox="0 0 54 72" aria-hidden="true">
      <defs><filter id="bpine-b"><feGaussianBlur stdDeviation="3" /></filter></defs>
      <ellipse cx="27" cy="34" rx="22" ry="28" fill={snow ? '#CBE0E8' : '#6BA85A'} filter="url(#bpine-b)" opacity="0.55" />
      <rect x="23" y="56" width="8" height="12" fill="#8C6239" stroke={SINK} strokeWidth="2" />
      <path d="M27 6 L44 30 L34 30 L48 50 L6 50 L20 30 L10 30 Z" fill={snow ? '#EAF2F5' : '#5E9A4C'} stroke={SINK} strokeWidth="2.6" strokeLinejoin="round" />
    </svg>
  );
}
function BChristmasTree({ s = 1 }) {
  return (
    <svg width={56 * s} height={72 * s} viewBox="0 0 56 72" aria-hidden="true">
      <rect x="24" y="56" width="8" height="12" fill="#8C6239" stroke={SINK} strokeWidth="2" />
      <path d="M28 6 L46 32 L36 32 L50 52 L6 52 L20 32 L10 32 Z" fill="#5E9A4C" stroke={SINK} strokeWidth="2.6" strokeLinejoin="round" />
      {[[20, 40], [34, 42], [28, 30], [24, 48], [38, 38]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.8" fill={['#E0574B', '#EBC43F', '#3E91C7', '#C96BA4', '#E8913C'][i]} stroke={SINK} strokeWidth="0.8" />)}
      <path d="M28 4 l1.6 3.4 3.4.4-2.6 2.2.8 3.6-3.2-2-3.2 2 .8-3.6-2.6-2.2 3.4-.4z" fill="#EBC43F" stroke={SINK} strokeWidth="0.6" />
    </svg>
  );
}
function BCloud({ s = 1 }) {
  return (
    <svg width={120 * s} height={64 * s} viewBox="0 0 120 64" aria-hidden="true">
      <defs><filter id="bcl-b"><feGaussianBlur stdDeviation="4" /></filter></defs>
      <path d="M22 50 C8 50 6 32 22 30 C22 14 46 12 50 26 C58 12 86 16 84 32 C102 32 102 50 86 50 Z" fill="#fff" filter="url(#bcl-b)" opacity="0.8" />
      <path d="M22 50 C8 50 6 32 22 30 C22 14 46 12 50 26 C58 12 86 16 84 32 C102 32 102 50 86 50 Z" fill="none" stroke={SINK} strokeWidth="2.4" strokeLinejoin="round" />
    </svg>
  );
}
function BSnow({ s = 1 }) {
  return (
    <svg width={44 * s} height={44 * s} viewBox="0 0 44 44" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => { const a = (i / 6) * Math.PI * 2; const x = 22 + Math.cos(a) * 16, y = 22 + Math.sin(a) * 16; return <g key={i} stroke="#5C9CC4" strokeWidth="2.4" strokeLinecap="round"><line x1="22" y1="22" x2={x} y2={y} /></g>; })}
      <circle cx="22" cy="22" r="3" fill="#5C9CC4" />
    </svg>
  );
}
function BPlane({ s = 1 }) {
  return (
    <svg width={64 * s} height={50 * s} viewBox="0 0 64 50" aria-hidden="true">
      <path d="M6 30 L50 22 L58 16 L52 28 L28 36 L22 46 L18 36 L6 34 Z" fill="#fff" stroke={SINK} strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M44 10 q6 -2 8 4" fill="none" stroke={SINK} strokeWidth="1.6" strokeDasharray="2 3" />
    </svg>
  );
}
// stick-figure skier (smiling), constant ink
function Skier({ s = 1, color = '#E0574B' }) {
  return (
    <svg width={54 * s} height={64 * s} viewBox="0 0 54 64" aria-hidden="true">
      <g fill="none" stroke={SINK} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="28" cy="12" r="7" fill="#F0DBB6" />
        <circle cx="25.5" cy="11" r="1.2" fill={SINK} /><circle cx="30.5" cy="11" r="1.2" fill={SINK} />
        <path d="M25 15 q3 3 6 0" strokeWidth="1.4" />
        <path d="M28 19 L26 36" stroke={color} strokeWidth="5" />
        <path d="M26 24 L14 30 M26 24 L40 22" stroke={color} strokeWidth="4" />
        <path d="M26 36 L16 50 M26 36 L36 50" strokeWidth="3.4" />
        {/* skis */}
        <path d="M8 52 L26 50 M30 50 L48 52" strokeWidth="3" />
        {/* poles */}
        <path d="M14 30 L10 52 M40 22 L44 50" strokeWidth="2" />
      </g>
    </svg>
  );
}

// ── BlakeAlps — the front-page scene (refined, age-appropriate) ──
function BlakeAlps({ width = 1180, height = 520 }) {
  // skier positions along the right-hand piste (grounded, rotated to the slope)
  const skiers = [
    { x: 808, y: 250, s: 0.8, r: 20, c: '#E0574B' },
    { x: 900, y: 296, s: 0.72, r: 20, c: '#3E91C7' },
    { x: 988, y: 338, s: 0.66, r: 20, c: '#5BA85A' },
  ];
  // sheep grounded across the foreground snow (baseline ~ y of each)
  const sheep = [
    { x: 250, y: 372, size: 132, p: DEREK },
    { x: 470, y: 392, size: 104, p: { sx: 1, sy: 1, mood: 'calm', eyes: 'happy' } },
    { x: 980, y: 372, size: 120, p: { sx: 1, sy: 1, mood: 'warm', eyes: 'closed' } },
    { x: 1108, y: 392, size: 96, p: { sx: 1, sy: 1, mood: 'dream', eyes: 'happy' } },
  ];
  return (
    <div style={{ position: 'relative', width, height, borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 36px rgba(40,30,12,0.22)' }}>
      <svg width={width} height={height} viewBox="0 0 1180 520" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
        <defs>
          <linearGradient id="alps-sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#AED6EC" /><stop offset="55%" stopColor="#D8EBF2" /><stop offset="100%" stopColor="#F3F7F4" /></linearGradient>
          <filter id="alps-soft" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="8" /></filter>
        </defs>
        <rect width="1180" height="520" fill="url(#alps-sky)" />

        {/* far range — soft wash + elegant ridgeline */}
        <path d="M0 318 L120 214 Q168 168 206 214 L300 120 Q344 92 388 140 L474 222 Q524 188 566 226 L660 138 Q712 108 760 158 L858 110 Q908 86 956 140 L1058 214 Q1116 188 1180 220 L1180 520 L0 520 Z" fill="#BFD6E6" filter="url(#alps-soft)" opacity="0.75" />
        <path d="M0 322 L120 218 Q168 172 206 218 L300 124 Q344 96 388 144 L474 226 Q524 192 566 230 L660 142 Q712 112 760 162 L858 114 Q908 90 956 144 L1058 218 Q1116 192 1180 224" fill="none" stroke={SINK} strokeWidth="2.4" strokeLinejoin="round" opacity="0.85" />
        {/* snow caps on the main summits */}
        {[[300, 124], [660, 142], [858, 114]].map(([x, y], i) => (
          <path key={i} d={`M${x - 30} ${y + 34} Q${x - 14} ${y + 18} ${x} ${y} Q${x + 16} ${y + 16} ${x + 30} ${y + 34} Q${x + 10} ${y + 24} ${x} ${y + 30} Q${x - 12} ${y + 24} ${x - 30} ${y + 34} Z`} fill="#FFFFFF" stroke={SINK} strokeWidth="1.6" />
        ))}

        {/* near range — darker, closer */}
        <path d="M0 380 L150 296 Q198 264 250 304 L360 232 Q414 204 466 250 L590 338 Q642 312 692 344 L812 262 Q872 234 930 286 L1050 366 Q1118 344 1180 372 L1180 520 L0 520 Z" fill="#A9C2D6" filter="url(#alps-soft)" opacity="0.8" />
        <path d="M0 384 L150 300 Q198 268 250 308 L360 236 Q414 208 466 254 L590 342 Q642 316 692 348 L812 266 Q872 238 930 290 L1050 370 Q1118 348 1180 376" fill="none" stroke={SINK} strokeWidth="2.6" strokeLinejoin="round" />
        {/* a few ridge shading strokes */}
        <g stroke={SINK} strokeWidth="1" opacity="0.3" fill="none">
          <path d="M360 240 l24 70 M812 270 l22 64 M250 312 l-18 56" />
        </g>

        {/* foreground snow field — clean, where the sheep stand */}
        <path d="M0 404 Q 300 430 620 452 T 1180 470 L1180 520 L0 520 Z" fill="#F6FAFC" />
        <path d="M0 404 Q 300 430 620 452 T 1180 470" fill="none" stroke={SINK} strokeWidth="2" opacity="0.4" />
        <g stroke="#9FB6C4" strokeWidth="1.4" opacity="0.45" fill="none"><path d="M120 470 q120 -8 260 4 M620 488 q160 -6 300 2" /></g>

        {/* ski lift — slim cable, pylons, gondola cabins */}
        <line x1="690" y1="120" x2="1150" y2="244" stroke={SINK} strokeWidth="2" />
        {[[760, 138], [1010, 206]].map(([x, y], i) => <line key={i} x1={x} y1={y} x2={x} y2={y + 70} stroke={SINK} strokeWidth="2.4" />)}
        {[[840, 158], [948, 188]].map(([x, y], i) => (
          <g key={i} stroke={SINK} strokeWidth="1.8" fill="#EBC43F"><line x1={x} y1={y} x2={x} y2={y + 14} /><rect x={x - 9} y={y + 14} width="18" height="14" rx="3" /></g>
        ))}
      </svg>

      {/* refined snowy pines, on the snow */}
      <div style={{ position: 'absolute', left: 56, bottom: 60 }}><BPine s={1.25} snow /></div>
      <div style={{ position: 'absolute', left: 132, bottom: 44 }}><BPine s={0.85} snow /></div>
      <div style={{ position: 'absolute', left: 700, bottom: 36 }}><BPine s={0.8} snow /></div>

      {/* skiers on the right-hand piste */}
      {skiers.map((k, i) => (
        <div key={i} style={{ position: 'absolute', left: k.x, top: k.y, transform: `rotate(${k.r}deg)` }}><Skier s={k.s} color={k.c} /></div>
      ))}

      {/* Magic Sheep — grounded on the foreground snow, with contact shadows */}
      {sheep.map((sh, i) => (
        <div key={i} style={{ position: 'absolute', left: sh.x, top: sh.y, transform: 'translateX(-50%)' }}>
          <div style={{ position: 'absolute', left: '50%', bottom: 6, transform: 'translateX(-50%)', width: sh.size * 0.5, height: sh.size * 0.1, borderRadius: '50%', background: 'rgba(40,40,60,0.18)', filter: 'blur(4px)' }} />
          <BlakeSheep size={sh.size} p={sh.p} />
        </div>
      ))}

      {/* sun */}
      <div style={{ position: 'absolute', right: 60, top: 26 }}><BSun s={1.5} /></div>

      {/* title — on a soft cream panel for legibility */}
      <div style={{ position: 'absolute', left: 40, top: 40, width: 560, background: 'rgba(251,246,232,0.66)', backdropFilter: 'blur(2px)', borderRadius: 14, padding: '18px 22px' }}>
        <div style={{ fontFamily: '"Nunito",sans-serif', fontSize: 12, letterSpacing: 2.4, textTransform: 'uppercase', color: '#5E6B74', fontWeight: 800 }}>for Isabella &amp; Isaac · a secret newsletter</div>
        <div style={{ fontFamily: '"Schoolbell",cursive', fontSize: 90, color: '#2A2218', lineHeight: 1.0, marginTop: 4 }}>Magic Sheep</div>
        <div style={{ marginTop: 10, fontFamily: '"Schoolbell",cursive', fontSize: 25, color: '#3B4A52', maxWidth: 470, lineHeight: 1.25 }}>
          live from the Alps this week — sunny slopes, a wonky ski-lift, and a flock who write you letters ✦
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { BlakeAlps, BSun, BPine, BChristmasTree, BCloud, BSnow, BPlane, Skier });
// backgrounds.jsx — 10 front-page background scenes in the Steve Leung style
// Wrapped in an IIFE so its many top-level names can't collide with other scripts.
(function () {
const LINK = '#2B2622';
const W = 1180, H = 520;

function ln(s) { const x = Math.sin(s * 53.7 + 7.1) * 9999; return x - Math.floor(x); }

// smooth ridge through alternating peak/saddle vertices (quadratic-midpoint spline)
function ridgePath(base, top, peaks, seed) {
  const seg = W / peaks, pts = [];
  for (let i = 0; i <= peaks; i++) {
    const x = i * seg;
    const y = (i % 2 === 0) ? top + ln(seed + i) * 26 : (top + base) / 2 - 10 + ln(seed + i * 2) * 20;
    pts.push([x, y]);
  }
  const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  let d = `M0 ${base} L ${pts[0][0]} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) { const m = mid(pts[i], pts[i + 1]); d += ` Q ${pts[i][0]} ${pts[i][1].toFixed(1)} ${m[0]} ${m[1].toFixed(1)}`; }
  d += ` L ${W} ${pts[pts.length - 1][1].toFixed(1)} L ${W} ${base} Z`;
  return { d, pts };
}

// a shaded mountain range: soft wash + ink ridge + right-flank shading + optional snow caps
function Range({ base, top, peaks, seed, fill, shade, caps }) {
  const { d, pts } = ridgePath(base, top, peaks, seed);
  return (
    <g>
      {/* ghosted wash confined to the silhouette (soft blur, faint ridge) */}
      <path d={d} fill={fill} filter="url(#bg-soft)" opacity="0.78" />
      <path d={d} fill={shade} opacity="0.32" transform="translate(6 12) scale(0.97)" style={{ transformOrigin: '590px 420px' }} />
      <path d={d} fill="none" stroke={LINK} strokeWidth="1.4" strokeLinejoin="round" opacity="0.2" />
      {caps && pts.filter((_, i) => i % 2 === 0).map(([x, y], i) => (
        <path key={i} d={`M${x - 26} ${y + 32} Q${x - 12} ${y + 14} ${x} ${y} Q${x + 13} ${y + 14} ${x + 26} ${y + 32} Q${x + 9} ${y + 22} ${x} ${y + 27} Q${x - 10} ${y + 22} ${x - 26} ${y + 32} Z`} fill="#fff" filter="url(#bg-soft)" opacity="0.85" />
      ))}
    </g>
  );
}

// cloud canopy path (single non-overlapping bumpy shape, like the sheep wool)
function treeCloud(rx, ry, seed) {
  const n = 9, vin = 0.8, pts = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    const j = 1 + (ln(seed + i) - 0.5) * 0.16;
    pts.push([Math.cos(a) * rx * vin * j, Math.sin(a) * ry * vin * j]);
  }
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i++) {
    const p = pts[i], q = pts[(i + 1) % n];
    const chord = Math.hypot(q[0] - p[0], q[1] - p[1]);
    d += ` A ${(chord * 0.66).toFixed(1)} ${(chord * 0.66).toFixed(1)} 0 0 1 ${q[0].toFixed(1)} ${q[1].toFixed(1)}`;
  }
  return d + ' Z';
}

// round splined tree — soft blurred low-opacity wash behind a single crisp ink
// cloud outline (matching the Magic Sheep weighting). No overlapping blobs.
function RoundTree({ x, y, s = 1, canopy = '#5E9A4C', shade = '#3F7A38', trunk = '#8C6239', snow = false, blossom = false, seed }) {
  const r = 48 * s;
  const sd = seed != null ? seed : Math.round(x + y);
  const cloud = treeCloud(r, r * 0.92, sd);
  return (
    <g transform={`translate(${x} ${y - r * 0.3})`}>
      <ellipse cx="0" cy={r * 1.35} rx={r * 1.05} ry={r * 0.26} fill="rgba(40,40,60,0.14)" />
      <rect x={-4 * s} y={r * 0.6} width={8 * s} height={r * 0.9} fill={trunk} stroke={LINK} strokeWidth="2" rx={3} />
      {/* soft blurred wash (reduced opacity, escapes the line a touch) */}
      <path d={cloud} fill={canopy} filter="url(#bg-soft)" opacity="0.5" transform="translate(3 4)" />
      <path d={cloud} fill={canopy} opacity="0.32" />
      {/* crisp ink cloud outline — constant weight */}
      <path d={cloud} fill="none" stroke={LINK} strokeWidth="2.4" strokeLinejoin="round" />
      {snow && <path d={cloud} fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="6 22" opacity="0.8" />}
      {blossom && [[-0.5, 0.1], [0.5, 0.1], [0, -0.45], [0.22, -0.15], [-0.25, -0.15]].map(([dx, dy], i) => <circle key={i} cx={dx * r} cy={dy * r} r={r * 0.13} fill="#E8A6C4" opacity="0.8" />)}
    </g>
  );
}

// rounded skier figure (NOT a stick man): round head + bobble hat, plump jacket, tucked legs on skis, poles
function RoundSkier({ x, y, s = 1, r = 18, jacket = '#E0574B' }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>
      <g stroke={LINK} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round">
        {/* skis */}
        <path d="M-26 30 q-4 4 0 8 l40 -4 q4 0 4 -4" fill="#9AA6AE" />
        {/* jacket — plump rounded body */}
        <path d="M-1 -2 C 14 -2 18 14 12 24 C 6 32 -10 32 -14 22 C -18 12 -14 -2 -1 -2 Z" fill={jacket} />
        {/* arm + pole */}
        <path d="M10 8 q12 2 14 16" fill="none" stroke={jacket} strokeWidth="6" />
        <path d="M24 4 l2 26" stroke={LINK} strokeWidth="2" />
        {/* legs tucked */}
        <path d="M-8 24 q-6 6 -10 6 M6 26 q6 4 12 2" fill="none" stroke="#3B4A52" strokeWidth="6" />
        {/* head */}
        <circle cx="2" cy="-12" r="10" fill="#F0DBB6" />
        <circle cx="-1" cy="-13" r="1.4" fill={LINK} /><circle cx="5" cy="-13" r="1.4" fill={LINK} />
        <path d="M-1 -8 q3 3 6 0" strokeWidth="1.4" fill="none" />
        {/* bobble hat */}
        <path d="M-8 -16 q10 -12 20 0 Z" fill={jacket} />
        <circle cx="2" cy="-26" r="3" fill="#fff" stroke={LINK} strokeWidth="1.4" />
      </g>
    </g>
  );
}

// small extras
const Moon = ({ x, y, r = 26 }) => <g><circle cx={x} cy={y} r={r} fill="#F6EFD0" stroke={LINK} strokeWidth="2" /><circle cx={x + r * 0.3} cy={y - r * 0.2} r={r * 0.85} fill="#1E2A4A" /></g>;
const Stars = ({ n = 30, seed = 1, color = '#FBE7A6' }) => <g>{Array.from({ length: n }).map((_, i) => { const x = ln(seed + i) * W, y = ln(seed + i * 3) * 260; return <circle key={i} cx={x} cy={y} r={1 + ln(i) * 1.6} fill={color} />; })}</g>;
const Cloud2 = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`}><path d="M0 30 C-12 30 -14 14 0 12 C2 -2 24 -2 26 12 C40 8 52 18 48 30 Z" fill="#fff" stroke={LINK} strokeWidth="2.2" opacity="0.95" /></g>;
const Boat = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2.4" strokeLinejoin="round"><path d="M-26 8 L26 8 L18 24 L-18 24 Z" fill="#C25A3E" /><path d="M0 8 L0 -28 L22 -2 Z" fill="#F4E3B0" /><path d="M0 8 L0 -28" /></g>;
const Cottage = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2.4" strokeLinejoin="round"><rect x="-26" y="0" width="52" height="36" fill="#F2E3C4" /><path d="M-32 0 L0 -26 L32 0 Z" fill="#B5503C" /><rect x="-8" y="14" width="16" height="22" fill="#7A5A3A" /><rect x="12" y="8" width="10" height="10" fill="#BFE0F2" /></g>;
const Tent = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2.4" strokeLinejoin="round"><path d="M-30 24 L0 -22 L30 24 Z" fill="#E0884B" /><path d="M0 -22 L0 24 M-8 24 L0 6 L8 24 Z" fill="#7A4A2A" /></g>;
const Campfire = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2"><path d="M0 0 C-8 -10 6 -14 0 -26 C12 -18 10 -6 0 0 Z" fill="#E8913C" /><path d="M-12 2 l24 6 M12 2 l-24 6" /></g>;
const Cactus = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2.4" strokeLinejoin="round" fill="#5E9A4C"><path d="M-6 30 L-6 -10 q0 -12 12 -12 q12 0 12 12 L18 30 Z" /><path d="M-6 6 q-14 0 -14 -14 q0 -8 6 -8" fill="none" stroke="#5E9A4C" strokeWidth="9" /></g>;
const Fish = ({ x, y, s = 1, c = '#E8913C' }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2" strokeLinejoin="round"><path d="M-14 0 Q0 -10 14 0 Q0 10 -14 0 Z" fill={c} /><path d="M14 0 l10 -7 l0 14 Z" fill={c} /><circle cx="-6" cy="-2" r="1.6" fill={LINK} /></g>;
const SharkFin = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`}><path d="M0 0 Q22 -6 30 -30 Q14 -10 0 0 Z" fill="#7E8C98" stroke={LINK} strokeWidth="2" strokeLinejoin="round" /></g>;
const Snorkel = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2.4" fill="none"><circle cx="0" cy="0" r="12" fill="#BFE0F2" /><path d="M11 -6 l8 -2 l0 -16" /><rect x="16" y="-26" width="6" height="6" fill="#E0574B" stroke={LINK} strokeWidth="1.6" /></g>;
const BeachBall = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="2"><circle cx="0" cy="0" r="13" fill="#fff" /><path d="M0 -13 A13 13 0 0 1 0 13" fill="#E0574B" /><path d="M-13 0 A13 13 0 0 1 13 0" fill="#3E91C7" opacity="0.5" /></g>;
const Butterfly = ({ x, y, s = 1, c = '#C96BA4' }) => <g transform={`translate(${x} ${y}) scale(${s})`} stroke={LINK} strokeWidth="1.6"><path d="M0 0 Q-12 -12 -16 -2 Q-12 8 0 2 Z" fill={c} /><path d="M0 0 Q12 -12 16 -2 Q12 8 0 2 Z" fill={c} /><line x1="0" y1="-2" x2="0" y2="6" /></g>;
const Tumbleweed = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`} fill="none" stroke="#9A7A42" strokeWidth="1.6"><circle cx="0" cy="0" r="14" /><path d="M-14 0 L14 0 M0 -14 L0 14 M-10 -10 L10 10 M-10 10 L10 -10" /></g>;

const SCENES = [
  { id: 'alps', name: 'The Alps', sky: ['#AED6EC', '#D8EBF2', '#F3F7F4'], back: ['#BFD6E6', '#7E9BB0'], front: ['#A9C2D6', '#6E8AA0'], caps: true, ground: '#F6FAFC', sun: true, skilift: true, story: 'Derek Hits the Slopes',
    trees: [{ x: 90, y: 430, s: 1.2, snow: true }, { x: 160, y: 446, s: 0.85, snow: true }, { x: 740, y: 452, s: 0.9, snow: true }],
    skiers: [{ x: 850, y: 250, s: 1, r: 20, c: '#E0574B' }, { x: 940, y: 296, s: 0.85, r: 20, c: '#3E91C7' }],
    sheep: [{ x: 280, baseY: 480, size: 128, mood: 'rainbow' }, { x: 470, baseY: 492, size: 100, mood: 'calm' }, { x: 1010, baseY: 480, size: 118, mood: 'warm' }], tag: 'the Alps' },
  { id: 'meadow', name: 'Summer meadow', sky: ['#9FD3F0', '#C9EAF4', '#EAF6E2'], back: ['#A7C98C', '#6E9A52'], front: ['#8FBF6A', '#5E8C3E'], ground: '#9FCB6A', sun: true, story: 'Derek Has a Picnic',
    trees: [{ x: 110, y: 430, s: 1.3, canopy: '#5E9A4C' }, { x: 1000, y: 440, s: 1.1, canopy: '#6BA85A' }, { x: 1090, y: 456, s: 0.8, canopy: '#5E9A4C' }],
    sheep: [{ x: 300, baseY: 486, size: 130, mood: 'rainbow' }, { x: 520, baseY: 496, size: 100, mood: 'meadow' }, { x: 760, baseY: 488, size: 112, mood: 'dream' }], flowers: true, butterflies: true, tag: 'a summer meadow' },
  { id: 'dusk', name: 'Snow at dusk', sky: ['#5E5A98', '#B07FA8', '#F0B68C'], back: ['#8E86B0', '#5C5680'], front: ['#6E6A96', '#46426A'], caps: true, ground: '#E6DCEC', moon: true, stars: true, story: 'Derek Goes Stargazing',
    trees: [{ x: 100, y: 432, s: 1.1, snow: true, canopy: '#4E6A5C' }, { x: 1060, y: 446, s: 0.95, snow: true, canopy: '#4E6A5C' }],
    sheep: [{ x: 300, baseY: 484, size: 122, mood: 'dream' }, { x: 900, baseY: 484, size: 112, mood: 'calm' }], tag: 'snow at dusk' },
  { id: 'seaside', name: 'The seaside', sky: ['#8FD0EE', '#CFEDF6', '#F6ECCB'], sea: true, ground: '#F0DFA8', sun: true, story: 'Derek Goes Snorkelling',
    trees: [{ x: 1090, y: 430, s: 1.1, canopy: '#5E9A4C' }],
    boats: [{ x: 720, y: 300, s: 1 }, { x: 880, y: 318, s: 0.7 }],
    sheep: [{ x: 280, baseY: 492, size: 126, mood: 'rainbow' }, { x: 470, baseY: 500, size: 96, mood: 'warm' }, { x: 980, baseY: 492, size: 110, mood: 'calm' }], sealife: true, tag: 'the seaside' },
  { id: 'autumn', name: 'Autumn woods', sky: ['#BFE0EC', '#E6EFE0', '#F4E8CE'], back: ['#C9A86A', '#9A7A42'], front: ['#C98A4A', '#9A5E2A'], ground: '#C4A45E', story: 'Derek Kicks the Leaves',
    trees: [{ x: 110, y: 426, s: 1.3, canopy: '#E0884B', shade: '#B5602A' }, { x: 270, y: 446, s: 0.9, canopy: '#D8A23C', shade: '#A8761F' }, { x: 1000, y: 436, s: 1.15, canopy: '#C25A3E', shade: '#923C24' }, { x: 1090, y: 452, s: 0.8, canopy: '#E0884B', shade: '#B5602A' }],
    leaves: true, sheep: [{ x: 420, baseY: 486, size: 124, mood: 'warm' }, { x: 640, baseY: 492, size: 104, mood: 'fiery' }, { x: 820, baseY: 486, size: 112, mood: 'rainbow' }], tag: 'autumn woods' },
  { id: 'england', name: 'English countryside', sky: ['#AFC9DE', '#D2E2EC', '#EAF1E2'], back: ['#A7C98C', '#74995A'], front: ['#8FBF6A', '#5E8C3E'], ground: '#92BB60', clouds: true, cottage: true, story: 'Derek in the Country',
    trees: [{ x: 120, y: 430, s: 1.25, canopy: '#5E9A4C' }, { x: 1080, y: 444, s: 1, canopy: '#6BA85A' }],
    sheep: [{ x: 320, baseY: 488, size: 126, mood: 'rainbow' }, { x: 560, baseY: 496, size: 98, mood: 'meadow' }, { x: 820, baseY: 488, size: 110, mood: 'calm' }], tag: 'the English countryside' },
  { id: 'night', name: 'Under the stars', sky: ['#10183A', '#26305C', '#3E4A78'], back: ['#2A3458', '#181E3C'], front: ['#222A4E', '#10163A'], ground: '#2E386040', moon: true, stars: true, campfire: true, tent: true, story: 'Derek Goes Camping',
    trees: [{ x: 120, y: 432, s: 1.1, canopy: '#2E4A40' }, { x: 1060, y: 446, s: 0.95, canopy: '#2E4A40' }],
    sheep: [{ x: 360, baseY: 484, size: 118, mood: 'dream' }, { x: 880, baseY: 484, size: 108, mood: 'calm' }], dark: true, tag: 'a night under the stars' },
  { id: 'lake', name: 'The lake', sky: ['#A6D6EC', '#D2EAF2', '#EAF4F0'], back: ['#BFD6E6', '#7E9BB0'], front: ['#A7C98C', '#6E9A52'], caps: true, lake: true, story: 'Derek Skips Stones',
    trees: [{ x: 110, y: 410, s: 1.2, canopy: '#5E9A4C' }, { x: 1080, y: 420, s: 1, canopy: '#6BA85A' }], sun: true,
    sheep: [{ x: 320, baseY: 470, size: 120, mood: 'rainbow' }, { x: 940, baseY: 470, size: 110, mood: 'meadow' }], lakefish: true, tag: 'the lake' },
  { id: 'blossom', name: 'Cherry blossom', sky: ['#CDEBF4', '#EAF2EC', '#F8E8EE'], back: ['#C9D9C4', '#9CB494'], front: ['#A7C98C', '#6E9A52'], ground: '#9FCB6A',
    trees: [{ x: 120, y: 422, s: 1.4, canopy: '#F4C2D6', shade: '#D98AAE', blossom: true }, { x: 300, y: 446, s: 0.95, canopy: '#F6CFE0', shade: '#D98AAE', blossom: true }, { x: 1000, y: 432, s: 1.2, canopy: '#F4C2D6', shade: '#D98AAE', blossom: true }],
    petals: true, sun: true, butterflies: true, story: 'Derek and the Blossom', sheep: [{ x: 470, baseY: 486, size: 122, mood: 'dream' }, { x: 700, baseY: 492, size: 102, mood: 'rainbow' }, { x: 880, baseY: 486, size: 110, mood: 'warm' }], tag: 'cherry blossom' },
  { id: 'desert', name: 'The desert', sky: ['#F4C98C', '#F6DDA8', '#F2E6C2'], back: ['#D9A96A', '#B5824A'], front: ['#E0B074', '#B5824A'], ground: '#E8C786', sun: true, mesa: true, tumble: true, story: 'Derek Rides a Camel',
    cacti: [{ x: 110, y: 432, s: 2.2 }, { x: 1040, y: 444, s: 1.7 }, { x: 1140, y: 460, s: 1.1 }],
    sheep: [{ x: 360, baseY: 486, size: 122, mood: 'warm' }, { x: 820, baseY: 488, size: 110, mood: 'fiery' }], tag: 'the desert' },
];

function SceneBg({ scene, width = 1180, height = 520, showTitle = true }) {
  const sc = scene;
  const uid = 'sc-' + sc.id;
  return (
    <div style={{ position: 'relative', width, height, borderRadius: 16, overflow: 'hidden', boxShadow: '0 16px 36px rgba(40,30,12,0.22)' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: W, height: H, transformOrigin: 'top left', transform: `scale(${width / W})` }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
        <defs>
          <linearGradient id={uid + '-sky'} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={sc.sky[0]} /><stop offset="55%" stopColor={sc.sky[1]} /><stop offset="100%" stopColor={sc.sky[2]} /></linearGradient>
          <filter id="bg-soft" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="8" /></filter>
        </defs>
        <rect width={W} height={H} fill={`url(#${uid}-sky)`} />
        {sc.stars && <Stars seed={sc.id.length + 2} />}
        {sc.id === 'dusk' && <g><path d="M520 70 Q 660 110 760 210" fill="none" stroke="#E0574B" strokeWidth="4" strokeLinecap="round" opacity="0.5" /><path d="M524 66 Q 664 106 764 206" fill="none" stroke="#5BA85A" strokeWidth="3" strokeLinecap="round" opacity="0.45" /><g transform="translate(520 70)"><circle r="7" fill="#fff" /><path d="M0 -12 L3 -3 12 0 3 3 0 12 -3 3 -12 0 -3 -3Z" fill="#FBE7A6" /></g></g>}
        {sc.moon && <Moon x={968} y={96} r={48} />}
        {sc.sun && <g><defs><filter id={uid + '-sun'}><feGaussianBlur stdDeviation="5" /></filter></defs><circle cx={1078} cy={88} r={34} fill="#F4C84A" filter={`url(#${uid}-sun)`} opacity="0.6" />{Array.from({ length: 12 }).map((_, i) => { const a = (i / 12) * Math.PI * 2; return <line key={i} x1={1078 + Math.cos(a) * 30} y1={88 + Math.sin(a) * 30} x2={1078 + Math.cos(a) * 42} y2={88 + Math.sin(a) * 42} stroke={LINK} strokeWidth="2.4" strokeLinecap="round" />; })}<circle cx={1078} cy={88} r={22} fill="#F6D45F" stroke={LINK} strokeWidth="2.8" /></g>}
        {sc.clouds && <><Cloud2 x={180} y={90} s={1.4} /><Cloud2 x={780} y={70} s={1.1} /></>}

        {sc.mesa && <g><path d="M0 300 L120 300 L150 240 L420 240 L450 300 L1180 300 L1180 360 L0 360 Z" fill={sc.back[0]} filter="url(#bg-soft)" opacity="0.85" /><path d="M150 240 L420 240" stroke={LINK} strokeWidth="2.4" /></g>}
        {sc.back && <Range base={360} top={150} peaks={8} seed={sc.id.length + 1} fill={sc.back[0]} shade={sc.back[1]} caps={sc.caps} />}
        {sc.front && <Range base={420} top={250} peaks={6} seed={sc.id.length + 5} fill={sc.front[0]} shade={sc.front[1]} caps={sc.caps} />}
        {sc.skilift && <g stroke={LINK} fill="none"><line x1="720" y1="150" x2="1140" y2="250" strokeWidth="2" /><line x1="812" y1="170" x2="812" y2="360" strokeWidth="3" /><line x1="1020" y1="220" x2="1020" y2="372" strokeWidth="3" /><g><line x1="900" y1="188" x2="900" y2="206" strokeWidth="2" /><rect x="888" y="206" width="24" height="16" rx="3" fill="#EBC43F" strokeWidth="2" /></g><g><line x1="1080" y1="244" x2="1080" y2="262" strokeWidth="2" /><rect x="1068" y="262" width="24" height="16" rx="3" fill="#EBC43F" strokeWidth="2" /></g></g>}

        {sc.sea && <g><rect x="0" y="300" width={W} height="120" fill="#5FA8C4" /><g stroke="#fff" strokeWidth="2" opacity="0.5" fill="none"><path d="M60 340 q20 -8 40 0 M300 360 q20 -8 40 0 M620 345 q20 -8 40 0 M900 365 q20 -8 40 0" /></g></g>}
        {/* ground */}
        {sc.ground && <path d={`M0 ${sc.lake ? 430 : 404} Q 300 ${sc.lake ? 446 : 430} 620 ${sc.lake ? 452 : 452} T ${W} ${sc.lake ? 470 : 470} L ${W} ${H} L0 ${H} Z`} fill={sc.ground} />}
        {sc.lake && <g><rect x="0" y="404" width={W} height="40" fill="#7FC0D8" opacity="0.9" /><g stroke="#fff" strokeWidth="1.6" opacity="0.5" fill="none"><path d="M120 420 q30 -6 60 0 M520 426 q30 -6 60 0 M860 418 q30 -6 60 0" /></g></g>}

        {/* trees in-scene */}
        {(sc.trees || []).map((t, i) => <RoundTree key={i} {...t} />)}
        {(sc.cacti || []).map((c, i) => <Cactus key={i} x={c.x} y={c.y} s={c.s} />)}
        {sc.cottage && <Cottage x={1000} y={392} s={2.4} />}
        {sc.tent && <Tent x={840} y={446} s={3.0} />}
        {sc.campfire && <Campfire x={560} y={494} s={2.4} />}
        {(sc.boats || []).map((b, i) => <Boat key={i} x={b.x} y={b.y} s={b.s} />)}
        {sc.leaves && Array.from({ length: 16 }).map((_, i) => <path key={i} d={`M${ln(i + 3) * W} ${ln(i * 2) * 320 + 60} q6 -6 12 0 q-2 8 -12 4 Z`} fill={['#E0884B', '#C25A3E', '#D8A23C'][i % 3]} opacity="0.8" />)}
        {sc.petals && Array.from({ length: 18 }).map((_, i) => <ellipse key={i} cx={ln(i + 5) * W} cy={ln(i * 3) * 340 + 50} rx="4" ry="2.4" fill="#F4C2D6" transform={`rotate(${ln(i) * 90} ${ln(i + 5) * W} ${ln(i * 3) * 340 + 50})`} />)}
        {sc.flowers && Array.from({ length: 22 }).map((_, i) => { const x = ln(i + 7) * W, y = 460 + ln(i * 2) * 50; return <g key={i}><circle cx={x} cy={y} r="3.5" fill={['#E0574B', '#EBC43F', '#C96BA4', '#fff'][i % 4]} stroke={LINK} strokeWidth="0.8" /><circle cx={x} cy={y} r="1.4" fill="#EBC43F" /></g>; })}
        {sc.sealife && <g><Fish x={150} y={360} s={1.2} c="#E8913C" /><Fish x={300} y={392} s={0.9} c="#C96BA4" /><Fish x={1040} y={372} s={1.1} c="#5BA85A" /><SharkFin x={840} y={332} s={1.1} /><BeachBall x={620} y={476} s={1.2} /><Snorkel x={210} y={470} s={1.1} /></g>}
        {sc.lakefish && <g><Fish x={520} y={420} s={1} c="#E8913C" /><Fish x={760} y={432} s={0.8} c="#C96BA4" /></g>}
        {sc.butterflies && Array.from({ length: 5 }).map((_, i) => <Butterfly key={'bf' + i} x={120 + i * 220} y={150 + ln(i * 4) * 160} s={0.9 + ln(i) * 0.5} c={['#C96BA4', '#E8913C', '#7B6FC4', '#E0574B', '#3E91C7'][i]} />)}
        {sc.tumble && <Tumbleweed x={560} y={486} s={1.3} />}
        {sc.id === 'alps' && <g>
          <path d="M80 434 Q 250 410 372 352 L374 434 Z" fill="#FFFFFF" stroke={LINK} strokeWidth="2.4" strokeLinejoin="round" />
          <path d="M300 248 Q 188 350 116 420" fill="none" stroke="#E0574B" strokeWidth="7" strokeLinecap="round" opacity="0.5" />
          <path d="M308 250 Q 196 352 124 422" fill="none" stroke="#EBC43F" strokeWidth="6" strokeLinecap="round" opacity="0.45" />
          <path d="M316 252 Q 204 354 132 424" fill="none" stroke="#3E91C7" strokeWidth="5" strokeLinecap="round" opacity="0.4" />
          {[[360, 330], [394, 350], [346, 300], [412, 326], [330, 270]].map((p, k) => <circle key={k} cx={p[0]} cy={p[1]} r={5 + (k % 3) * 2} fill="#fff" stroke={LINK} strokeWidth="1.2" />)}
        </g>}
        {sc.id === 'meadow' && <g transform="translate(540 488)">
          <ellipse cx="0" cy="8" rx="160" ry="42" fill="#E0574B" opacity="0.9" stroke={LINK} strokeWidth="2.4" />
          <g stroke="#fff" strokeWidth="2" opacity="0.5" fill="none"><path d="M-120 2 H120 M-120 16 H120 M-72 -16 V34 M0 -20 V36 M72 -16 V34" /></g>
          <g transform="translate(104 -4)"><path d="M-18 8 q18 -12 36 0 l-5 18 h-26 Z" fill="#C99A5A" stroke={LINK} strokeWidth="2" strokeLinejoin="round" /><path d="M-18 8 q18 9 36 0" fill="none" stroke={LINK} strokeWidth="2" /></g>
        </g>}
        {sc.id === 'night' && <g><circle cx="560" cy="472" r="78" fill="#F6A24A" opacity="0.3" filter="url(#bg-soft)" /><circle cx="560" cy="472" r="40" fill="#F6C24A" opacity="0.35" filter="url(#bg-soft)" /></g>}
      </svg>

      {/* skiers (rounded figures) — wrapped in svg so the <g> renders */}
      {(sc.skiers || []).map((k, i) => (
        <div key={i} style={{ position: 'absolute', left: k.x, top: k.y }}>
          <svg width="120" height="120" viewBox="-50 -50 120 120" style={{ overflow: 'visible' }}><RoundSkier x={0} y={0} s={k.s} r={k.r} jacket={k.c} /></svg>
        </div>
      ))}

      {/* grounded Magic Sheep — the big foreground subjects, doing the activity */}
      {(sc.sheep || []).map((sh, i) => {
        const size = Math.round(sh.size * (i === 0 ? 2.4 : 1.2));
        const gear = sh.gear ? sh.gear
          : sc.id === 'seaside' ? ['snorkel']
          : (sc.id === 'alps' && i === 0) ? ['goggles']
          : (sc.id === 'alps' && i === 2) ? ['scarf'] : [];
        const eyes = sh.eyes ? sh.eyes : (sc.id === 'dusk') ? 'up' : (i === 0 ? 'wide' : (i % 3 === 1 ? 'closed' : 'happy'));
        const foot = size * 0.84;
        return (
          <div key={i} style={{ position: 'absolute', left: sh.x, top: sh.baseY - foot, transform: 'translateX(-50%)', zIndex: 5 }}>
            <div style={{ position: 'absolute', left: '50%', top: foot - 6, transform: 'translateX(-50%)', width: size * 0.42, height: size * 0.08, borderRadius: '50%', background: 'rgba(20,20,40,0.24)', filter: 'blur(4px)' }} />
            <div style={{ filter: 'drop-shadow(0 3px 3px rgba(40,30,20,0.22))' }}><BlakeSheep size={size} p={{ sx: 1, sy: 1, mood: sh.mood, eyes, accessories: gear, tilt: (i === 0 && (sc.id === 'alps' || sc.id === 'lake')) ? -14 : 0 }} /></div>
          </div>
          );
      })}

      {(() => { const hx = 70 + (sc.id.length * 67) % 220, hy = sc.dark ? 250 : 300; return <div style={{ position: 'absolute', left: hx, top: hy, zIndex: 4, opacity: 0.95 }} title="a hidden Magic Sheep"><BlakeSheep size={66} p={{ sx: 1, sy: 1, mood: 'rainbow', eyes: 'happy' }} /></div>; })()}
      {showTitle && (
        <div style={{ position: 'absolute', left: 40, top: 36, width: 560, background: sc.dark ? 'rgba(16,20,40,0.5)' : 'rgba(251,246,232,0.62)', backdropFilter: 'blur(2px)', borderRadius: 14, padding: '16px 22px' }}>
          <div style={{ fontFamily: '"Nunito",sans-serif', fontSize: 12, letterSpacing: 2.4, textTransform: 'uppercase', color: sc.dark ? '#C7CEE0' : '#5E6B74', fontWeight: 800 }}>A Magic Sheep Adventure</div>
          <div style={{ fontFamily: '"Schoolbell",cursive', fontSize: 84, color: sc.dark ? '#F4ECD8' : '#2A2218', lineHeight: 1.0, marginTop: 2 }}>{sc.story || 'Magic Sheep'}</div>
        </div>
      )}
      </div>
    </div>
  );
}

Object.assign(window, { SCENES, SceneBg, RoundTree, RoundSkier });
})();
// content.jsx — TimelineRoad (winding road v2 with fun world objects) and
// MailboxFlow (handwrite → photograph → upload → Magic Sheep replies).

// ── decorative SVG objects ────────────────────────────────────
function Plane({ s = 1, color = '#C0664E' }) {
  return (
    <svg width={64 * s} height={64 * s} viewBox="0 0 64 64" aria-hidden="true">
      <path d="M6 34 L52 26 L58 22 L54 30 L30 40 L24 52 L20 42 L6 40 Z" fill="#FBF6E8" stroke={color} strokeWidth="2.4" strokeLinejoin="round" />
      <circle cx="48" cy="27" r="2" fill={color} />
    </svg>
  );
}
function Sun({ s = 1, color = '#E8A23C' }) {
  return (
    <svg width={70 * s} height={70 * s} viewBox="0 0 70 70" aria-hidden="true">
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        return <line key={i} x1={35 + Math.cos(a) * 20} y1={35 + Math.sin(a) * 20} x2={35 + Math.cos(a) * 30} y2={35 + Math.sin(a) * 30} stroke={color} strokeWidth="3" strokeLinecap="round" />;
      })}
      <circle cx="35" cy="35" r="17" fill="#F4D06A" stroke={color} strokeWidth="2.5" />
    </svg>
  );
}
function Snowflake({ s = 1, color = '#5C9CC4' }) {
  return (
    <svg width={48 * s} height={48 * s} viewBox="0 0 48 48" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => {
        const a = (i / 6) * Math.PI * 2;
        const x = 24 + Math.cos(a) * 18, y = 24 + Math.sin(a) * 18;
        return <g key={i} stroke={color} strokeWidth="2.4" strokeLinecap="round">
          <line x1="24" y1="24" x2={x} y2={y} />
          <line x1={24 + Math.cos(a) * 11} y1={24 + Math.sin(a) * 11} x2={24 + Math.cos(a) * 11 + Math.cos(a + 0.7) * 6} y2={24 + Math.sin(a) * 11 + Math.sin(a + 0.7) * 6} />
          <line x1={24 + Math.cos(a) * 11} y1={24 + Math.sin(a) * 11} x2={24 + Math.cos(a) * 11 + Math.cos(a - 0.7) * 6} y2={24 + Math.sin(a) * 11 + Math.sin(a - 0.7) * 6} />
        </g>;
      })}
    </svg>
  );
}
function ChristmasTree({ s = 1 }) {
  return (
    <svg width={56 * s} height={64 * s} viewBox="0 0 56 64" aria-hidden="true">
      <rect x="24" y="50" width="8" height="10" fill="#8C6239" />
      <path d="M28 6 L44 28 L34 28 L48 46 L8 46 L22 28 L12 28 Z" fill="#4F9A4A" stroke="#3B7A38" strokeWidth="2" strokeLinejoin="round" />
      {[[20, 38], [34, 40], [28, 30], [24, 44], [38, 36]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.6" fill={['#E0574B', '#EBC43F', '#3E91C7', '#C96BA4', '#E8913C'][i]} />)}
      <path d="M28 4 l1.6 3.4 3.4.4-2.6 2.2.8 3.6-3.2-2-3.2 2 .8-3.6-2.6-2.2 3.4-.4z" fill="#EBC43F" />
    </svg>
  );
}
function CloudPuff({ s = 1, fill = '#FFFFFF' }) {
  return (
    <svg width={120 * s} height={64 * s} viewBox="0 0 120 64" aria-hidden="true">
      <path d="M20 50 C6 50 4 32 20 30 C20 14 44 12 48 26 C56 12 84 16 82 32 C100 32 100 50 84 50 Z" fill={fill} stroke="#C9B7A0" strokeWidth="2" />
    </svg>
  );
}
function Suitcase({ s = 1, color = '#7B5BB8' }) {
  return (
    <svg width={56 * s} height={48 * s} viewBox="0 0 56 48" aria-hidden="true">
      <rect x="18" y="6" width="20" height="8" rx="3" fill="none" stroke={color} strokeWidth="3" />
      <rect x="8" y="14" width="40" height="28" rx="4" fill="#FBF6E8" stroke={color} strokeWidth="2.6" />
      <line x1="28" y1="14" x2="28" y2="42" stroke={color} strokeWidth="2.4" strokeDasharray="2 3" />
    </svg>
  );
}

// ── TimelineRoad — winding road; stops are angled photos + objects ──
const ROAD_STOPS = [
  { id: 'first', date: '~ Aug 2024', loc: 'the doorstep', color: '#C0664E', title: 'A sheep appears',
    body: 'A small figurine on the step. No note. The flock is real, somehow.',
    items: [{ type: 'sheep', cap: 'the first' }], deco: ['sun'] },
  { id: 'concert', date: 'Dec 2024', loc: 'home', color: '#5C8C3E', title: 'The Christmas concert',
    body: 'Piano → tap-dancing hooves. Violin → lambs asleep in harmony. The floor did not survive.',
    items: [{ type: 'photo', src: '/ms-assets/letter-christmas.jpeg', cap: 'the letter' }], deco: ['tree', 'snow'] },
  { id: 'yarn', date: 'Dec 2024', loc: 'Suk Suk → UK', color: '#2E847A', title: 'Magic Yarn arrives',
    body: 'Two rainbow loaves of chenille, flown across the world. Say "THANK YOU SUK SUK!" to use it.',
    items: [{ type: 'photo', src: '/ms-assets/magic-yarn.jpeg', cap: 'blessed chenille' }], deco: ['plane', 'cloud'] },
  { id: 'rainbow', date: 'Jan 2025', loc: 'the meadow', color: '#C95E84', title: 'A rainbow sheep joins',
    body: 'Hand-painted, a slot in the back for offerings. Currency: hopes, marbles, small wishes.',
    items: [{ type: 'sheep', cap: 'new arrival' }], deco: ['sun'] },
  { id: 'shear', date: '11 Feb 2026', loc: 'United Kingdom', color: '#7B5BB8', title: 'Let us SHEAR the truth',
    body: 'Broadcasting from the UK. The annual vote: 41 yes, 1 no. This year Derek voted Maybe. Progress.',
    items: [{ type: 'photo', src: '/ms-assets/letter-uk-open.jpeg', cap: 'page 1' }, { type: 'photo', src: '/ms-assets/letter-uk-reasons.jpeg', cap: 'page 2' }], deco: ['suitcase', 'cloud'] },
];

function decoNode(name, key) {
  const map = { plane: <BPlane s={1.1} />, sun: <BSun s={1.1} />, snow: <BSnow s={1.1} />, tree: <BChristmasTree s={1} />, cloud: <BCloud s={0.9} />, suitcase: <BPine s={0.9} /> };
  return <span key={key} style={{ display: 'inline-block' }}>{map[name]}</span>;
}

function RoadTile({ item, color, tilt, sheepStyle, w = 138 }) {
  const Style = sheepStyle || StylePomPom;
  return (
    <div style={{
      background: '#FBF6E8', padding: 9, paddingBottom: 26, width: w, position: 'relative',
      transform: `rotate(${tilt}deg)`, boxShadow: '0 9px 20px rgba(40,30,12,0.24)',
    }}>
      <div style={{ position: 'absolute', top: -11, left: w * 0.32, zIndex: 2 }}>
        <WashiTape width={58} color={color} pattern={['stripe', 'dots', 'check'][Math.abs(Math.round(tilt)) % 3]} rotate={-6} />
      </div>
      <div style={{ width: w - 18, height: w - 18, overflow: 'hidden', borderRadius: 4, background: item.type === 'sheep' ? '#F2E8D0' : '#222', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {item.type === 'sheep' ? <Style size={w - 18} /> : <img src={item.src} alt={item.cap} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </div>
      <div style={{ position: 'absolute', left: 9, right: 9, bottom: 4, textAlign: 'center', fontFamily: '"Schoolbell", cursive', fontSize: 16, color }}>{item.cap}</div>
    </div>
  );
}

function TimelineRoad({ font = '"Schoolbell", cursive', sheepStyle }) {
  const W = 1080, H = 1180;
  // hand-placed stop anchors along a winding road
  const pos = [
    { x: 120, y: 70, t: -5 },
    { x: 640, y: 210, t: 5 },
    { x: 200, y: 430, t: -4 },
    { x: 700, y: 660, t: 6 },
    { x: 240, y: 880, t: -3 },
  ];
  return (
    <div style={{ position: 'relative', width: W, height: H, borderRadius: 10, overflow: 'hidden', boxShadow: '0 14px 32px rgba(40,30,12,0.22)' }}>
      {/* scene-style sky → meadow wash (keeps the scrapbook paper edges) */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,#CDEAF4 0%,#E6EFD6 42%,#E0CFA8 100%)' }} />
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.4, mixBlendMode: 'multiply' }} aria-hidden="true">
        <filter id="road-tex"><feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" seed="9" /><feColorMatrix values="0 0 0 0 0.42 0 0 0 0 0.33 0 0 0 0 0.18 0 0 0 0.4 0" /></filter>
        <rect width="100%" height="100%" filter="url(#road-tex)" />
      </svg>
      {/* sun #1 + soft ghosted hills, matching the front-cover scenes */}
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
        <defs><filter id="road-soft" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="10" /></filter></defs>
        <path d={`M0 ${H * 0.30} Q ${W * 0.3} ${H * 0.22} ${W * 0.62} ${H * 0.28} T ${W} ${H * 0.26} L ${W} ${H} L0 ${H} Z`} fill="#9FB6C4" filter="url(#road-soft)" opacity="0.5" />
        <path d={`M0 ${H * 0.44} Q ${W * 0.35} ${H * 0.36} ${W * 0.7} ${H * 0.44} T ${W} ${H * 0.42} L ${W} ${H} L0 ${H} Z`} fill="#A7C98C" filter="url(#road-soft)" opacity="0.55" />
      </svg>
      <div style={{ position: 'absolute', top: 26, right: 40 }}><BSun s={1.6} /></div>
      {/* faint dotted grid for "map" feel */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(90,70,40,0.12) 1px, transparent 1.6px)', backgroundSize: '26px 26px' }} />

      {/* rolling landscape so the journey sits in a world, not on floating bits */}
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
        <path d={`M0 ${H*0.34} C ${W*0.25} ${H*0.28}, ${W*0.5} ${H*0.4}, ${W*0.74} ${H*0.33} C ${W*0.9} ${H*0.29}, ${W} ${H*0.36}, ${W} ${H*0.36} L ${W} ${H} L 0 ${H} Z`} fill="#D7C79E" opacity="0.5" />
        <path d={`M0 ${H*0.58} C ${W*0.3} ${H*0.5}, ${W*0.6} ${H*0.64}, ${W} ${H*0.56} L ${W} ${H} L 0 ${H} Z`} fill="#CDBC8E" opacity="0.55" />
        <path d={`M0 ${H*0.82} C ${W*0.35} ${H*0.74}, ${W*0.65} ${H*0.88}, ${W} ${H*0.8} L ${W} ${H} L 0 ${H} Z`} fill="#C3B07E" opacity="0.6" />
      </svg>

      {/* winding dashed road */}
      <svg width={W} height={H} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
        <path d={`M 180 110 C 520 150, 520 250, 700 260 C 860 270, 820 430, 320 470 C 120 486, 200 690, 740 700 C 980 706, 760 900, 320 920`}
          fill="none" stroke="#B5683F" strokeWidth="6" strokeDasharray="2 16" strokeLinecap="round" opacity="0.65" />
      </svg>

      {/* floating decorative objects — Blake style */}
      <div style={{ position: 'absolute', top: 30, right: 60 }}><BSun s={1.4} /></div>
      <div style={{ position: 'absolute', top: 120, left: 360, opacity: 0.95 }}><BCloud s={1.1} /></div>
      <div style={{ position: 'absolute', top: 540, right: 120 }}><BCloud s={0.8} /></div>
      <div style={{ position: 'absolute', bottom: 120, right: 220 }}><BPlane s={1.3} /></div>

      {/* stops */}
      {ROAD_STOPS.map((e, i) => {
        const p = pos[i];
        return (
          <div key={e.id} style={{ position: 'absolute', left: p.x, top: p.y, width: 360 }}>
            {/* numbered map pin */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {e.items.map((it, k) => <RoadTile key={k} item={it} color={e.color} tilt={p.t + (k ? 7 : 0)} sheepStyle={sheepStyle} w={132} />)}
              </div>
              <div style={{ paddingTop: 6 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 26, height: 26, borderRadius: '50%', background: e.color, color: '#FFF9EC', fontFamily: font, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                  <span style={{ fontFamily: font, fontSize: 20, color: e.color }}>{e.date}</span>
                </div>
                <div style={{ fontFamily: font, fontSize: 26, color: '#2A2218', lineHeight: 1.05, marginTop: 4, maxWidth: 200 }}>{e.title}</div>
                <div style={{ fontFamily: font, fontSize: 17, color: '#6B5E45' }}>📍 {e.loc}</div>
                <div style={{ fontFamily: '"Nunito", sans-serif', fontSize: 12.5, color: '#5C4F3A', lineHeight: 1.45, marginTop: 4, maxWidth: 210 }}>{e.body}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>{e.deco.map((d, k) => decoNode(d, k))}</div>
              </div>
            </div>
          </div>
        );
      })}

      {/* end flag */}
      <div style={{ position: 'absolute', left: 300, bottom: 40, fontFamily: font, fontSize: 24, color: '#7A6C50' }}>
        …and wherever the wind takes them next ✦
      </div>
    </div>
  );
}

// ── MailboxFlow — the handwrite → photograph → upload → reply concept ──
function MailboxFlow({ font = '"Schoolbell", cursive', sheepStyle }) {
  const Style = sheepStyle || StylePomPom;
  const steps = [
    { n: 1, color: '#E8913C', title: 'Write a letter', body: 'On real paper, with your best pens. Doodles, stickers, glitter — the messier and more magical, the better. Magic Sheep love effort.' },
    { n: 2, color: '#5BA85A', title: 'Take a photo', body: 'Snap your letter (and anything you made) with a phone or tablet camera.' },
    { n: 3, color: '#3E91C7', title: 'Post it to the mailbox', body: 'Drop the photo into the slot below. It flies off to the meadow.' },
    { n: 4, color: '#7B5BB8', title: 'Magic Sheep reply', body: 'In a few days a hand-written letter comes back — sometimes with a new sheep to add to your collection.' },
  ];
  return (
    <div style={{ width: 1080, display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* steps row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {steps.map((s) => (
          <div key={s.n} style={{ background: '#FBF6E8', borderRadius: 12, border: `1px solid ${s.color}44`, padding: '16px 16px 18px', boxShadow: '0 8px 18px rgba(40,30,12,0.14)', position: 'relative' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: s.color, color: '#FFF9EC', fontFamily: font, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</div>
            <div style={{ fontFamily: font, fontSize: 24, color: '#2A2218', marginTop: 8, lineHeight: 1 }}>{s.title}</div>
            <div style={{ fontFamily: '"Nunito", sans-serif', fontSize: 13, color: '#5C4F3A', lineHeight: 1.5, marginTop: 6 }}>{s.body}</div>
          </div>
        ))}
      </div>

      {/* the upload + reply panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
        {/* upload card */}
        <div data-controller="upload" style={{ background: '#FBF6E8', borderRadius: 14, border: '2px dashed #C0664E', padding: 22, position: 'relative' }}>
          <div style={{ position: 'absolute', top: -12, left: 28 }}><WashiTape width={92} color="#C0664E" pattern="dots" rotate={-5} /></div>
          <div style={{ fontFamily: font, fontSize: 28, color: '#C0664E' }}>📮 Post your letter</div>
          <div style={{ fontFamily: '"Nunito", sans-serif', fontSize: 13, color: '#6B5E45', marginTop: 2, marginBottom: 12 }}>
            Drag a photo of your letter onto the mailbox, or click to choose one.
          </div>
          <div data-ms-dropzone data-action="click->upload#pickPhoto dragover->upload#dragOver dragleave->upload#dragLeave drop->upload#dropPhoto"
            style={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 12, border: '2px dashed #C9A85E', background: '#FFFDF6', cursor: 'pointer', overflow: 'hidden', position: 'relative' }}>
            <span data-ms-drop-label style={{ fontFamily: font, fontSize: 22, color: '#9A8456' }}>📷 drop your letter photo here</span>
            <img data-ms-drop-preview alt="" style={{ display: 'none', width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <input data-ms-file type="file" accept="image/*" data-action="upload#fileChosen" style={{ display: 'none' }} />
          <button data-action="upload#pickPhoto" style={{
            marginTop: 14, width: '100%', cursor: 'pointer',
            fontFamily: font, fontSize: 22, color: '#FFF9EC', background: '#C0664E',
            border: 'none', borderRadius: 999, padding: '10px 0',
          }}>Send to the meadow ✦</button>
        </div>

        {/* reply preview */}
        <div style={{ position: 'relative' }}>
          {/* the sheep, peeking */}
          <div style={{ position: 'absolute', top: -28, right: 10, zIndex: 2 }}>
            <Style size={120} />
          </div>
          <div style={{
            background: 'repeating-linear-gradient(180deg,#FBF6E8 0 30px,#F4EBD2 30px 31px)',
            borderRadius: 14, border: '1px solid #D9C7A1', padding: '26px 24px', marginTop: 28,
            boxShadow: '0 10px 24px rgba(40,30,12,0.18)', minHeight: 300,
          }}>
            <div style={{ fontFamily: '"Nunito", sans-serif', fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase', color: '#8A7B5C', fontWeight: 800 }}>
              …a few days later
            </div>
            <div style={{ fontFamily: font, fontSize: 30, color: '#7B5BB8', marginTop: 6, lineHeight: 1.1 }}>
              Dear friend,
            </div>
            <div style={{ fontFamily: font, fontSize: 21, color: '#3B3220', lineHeight: 1.5, marginTop: 8 }}>
              We got your letter! It smelled faintly of <span style={{ color: '#5BA85A' }}>felt-tip</span> and
              <span style={{ color: '#E8913C' }}> excellent ideas</span>. Bramble read it aloud twice.
              Enclosed: one new sheep for your collection, and a small dare for next time…
            </div>
            <div style={{ fontFamily: font, fontSize: 22, color: '#C0664E', marginTop: 12 }}>— The Magic Sheep ✿</div>
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, {
  TimelineRoad, MailboxFlow, Plane, Sun, Snowflake, ChristmasTree, CloudPuff, Suitcase,
});
// mailbox-screens.jsx — the things we'd lost: a rack of nice wax-sealed
// envelopes, and an opened letter that re-types the real letters in a warm
// hand font with coloured ink. (Envelope lives in visual.jsx.)

const MS_LETTERS = [
  { id: 'shear', sender: 'The Magic Sheep', subject: 'Let us SHEAR the truth', date: '11 Feb · UK', color: '#F1E2BD', seal: '#C04432', accent: '#2E847A', isNew: true, tilt: -3 },
  { id: 'wool',  sender: 'Nimbus',          subject: 'A bulletin from Wool-Fi HQ', date: '02 Jan · Cloud 9', color: '#E5D7B3', seal: '#D67A6A', accent: '#3370A8', isNew: false, tilt: 2 },
  { id: 'yarn',  sender: 'Suk Suk',         subject: 'Your Magic Yarn is on its way', date: '20 Dec · Hong Kong', color: '#EFE4C8', seal: '#7B5BB8', accent: '#C95E84', isNew: false, tilt: -2 },
  { id: 'concert', sender: 'Sonata',        subject: 'About that piano performance…', date: '24 Dec · the meadow', color: '#EAD9B0', seal: '#5C8C3E', accent: '#74853A', isNew: false, tilt: 2 },
];

// ── Blake-style hand-drawn envelope (constant ink line + wax wash + sheep stamp) ──
function BlakeEnvelope({ width = 460, height = 188, color, seal, sender, subject, date, isNew, accent, font, tilt = 0 }) {
  const ink = '#2B2622';
  return (
    <div style={{ position: 'relative', width, height, transform: `rotate(${tilt}deg)`, filter: 'drop-shadow(0 8px 14px rgba(40,30,12,0.22))' }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
        {/* body — slightly wobbly rectangle */}
        <path d={`M6 10 Q ${width / 2} 4 ${width - 8} 9 Q ${width - 3} ${height / 2} ${width - 7} ${height - 8} Q ${width / 2} ${height - 3} 8 ${height - 7} Q 3 ${height / 2} 6 10 Z`} fill={color} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
        {/* flap — angular triangle (straight lips to a point) */}
        <path d={`M4 9 L ${width / 2} ${height * 0.52} L ${width - 6} 9`} fill={color} stroke={ink} strokeWidth="3" strokeLinejoin="round" />
        {/* wax seal — ink-ringed colour blob at flap tip */}
        <circle cx={width / 2} cy={height * 0.5} r="15" fill={seal} stroke={ink} strokeWidth="2.4" />
        <path d={`M${width / 2 - 5} ${height * 0.5} q5 5 10 0`} stroke="#fff" strokeWidth="1.6" fill="none" opacity="0.7" />
        {/* perforated postage stamp */}
        {(() => { const sx = width - 80, sy = 14, sw = 60, sh = 64, dots = []; for (let x = sx; x <= sx + sw; x += 10) { dots.push(['x', x, sy]); dots.push(['x', x, sy + sh]); } for (let y = sy; y <= sy + sh; y += 10) { dots.push(['y', sx, y]); dots.push(['y', sx + sw, y]); } return <g><rect x={sx} y={sy} width={sw} height={sh} fill="#FBF6E8" stroke={ink} strokeWidth="1.4" />{dots.map((d, k) => <circle key={k} cx={d[1]} cy={d[2]} r="2.4" fill={color} />)}<rect x={sx + 5} y={sy + 5} width={sw - 10} height={sh - 10} fill="none" stroke={ink} strokeWidth="1" opacity="0.45" /></g>; })()}
      </svg>
      {/* Derek on the stamp */}
      <div style={{ position: 'absolute', right: 21, top: 16, width: 58, height: 62, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <BlakeSheep size={62} p={DEREK} />
      </div>
      <div style={{ position: 'absolute', right: 21, top: 70, width: 58, textAlign: 'center', fontFamily: font, fontSize: 11, color: '#8A7B5C' }}>Derek</div>
      {/* address text */}
      <div style={{ position: 'absolute', left: 24, top: height * 0.42 + 10, right: 96 }}>
        <div style={{ fontFamily: '"Nunito",sans-serif', fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: '#7A6C50', fontWeight: 800 }}>from {sender}</div>
        <div style={{ fontFamily: font, fontSize: 23, color: accent, lineHeight: 1.18, marginTop: 5 }}>{subject}</div>
        <div style={{ fontFamily: font, fontSize: 16, color: '#8A7B5C', marginTop: 6 }}>{date}</div>
      </div>
      {isNew && <div style={{ position: 'absolute', left: -8, top: -10, fontFamily: font, fontSize: 18, color: '#fff', background: '#C04432', borderRadius: 999, padding: '1px 12px', transform: 'rotate(-8deg)', boxShadow: '0 2px 6px rgba(40,30,12,0.3)' }}>new!</div>}
    </div>
  );
}

// ── Mailbox rack ──────────────────────────────────────────────
function MailboxRack({ font = '"Schoolbell", cursive' }) {
  return (
    <div style={{ position: 'relative', width: 1080, borderRadius: 12, overflow: 'hidden', boxShadow: '0 14px 32px rgba(40,30,12,0.2)' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,#CDEAF4 0%,#E6EFD6 45%,#EBDDBC 100%)' }} />
      <svg width="100%" height="100%" viewBox="0 0 1080 520" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
        <defs><filter id="rack-soft" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="10" /></filter></defs>
        <path d="M0 150 Q 320 120 660 150 T 1080 150 L1080 520 L0 520 Z" fill="#9FB6C4" filter="url(#rack-soft)" opacity="0.45" />
        <path d="M0 210 Q 360 180 720 210 T 1080 205 L1080 520 L0 520 Z" fill="#A7C98C" filter="url(#rack-soft)" opacity="0.5" />
      </svg>
      <div style={{ position: 'absolute', top: 20, right: 34 }}><BSun s={1.5} /></div>
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.28, mixBlendMode: 'multiply' }} aria-hidden="true">
        <filter id="rack-tex"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" seed="5" /><feColorMatrix values="0 0 0 0 0.4 0 0 0 0 0.32 0 0 0 0 0.18 0 0 0 0.35 0" /></filter>
        <rect width="100%" height="100%" filter="url(#rack-tex)" />
      </svg>

      <div style={{ position: 'relative', padding: '28px 36px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: '"Nunito",sans-serif', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#8A7B5C', fontWeight: 800 }}>from the flock</div>
            <div style={{ fontFamily: font, fontSize: 54, color: '#2A2218', lineHeight: 0.9 }}>The Mailbox</div>
            <div style={{ marginLeft: 2 }}><Squiggle width={220} color="#C0664E" thickness={3} /></div>
          </div>
          <div style={{ fontFamily: font, fontSize: 22, color: '#C0664E', background: '#F0CD5A88', border: '2px dashed #C0664E', borderRadius: 999, padding: '4px 16px', transform: 'rotate(-3deg)' }}>1 unread ✦</div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'center' }}>
          {MS_LETTERS.map((L) => (
            <BlakeEnvelope key={L.id} width={462} height={190} color={L.color} seal={L.seal}
              sender={L.sender} subject={L.subject} date={L.date} isNew={L.isNew}
              accent={L.accent} font={font} tilt={L.tilt} />
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 22, fontFamily: font, fontSize: 20, color: '#8A7B5C' }}>
          ✿ older letters are tucked in the attic
        </div>
      </div>
    </div>
  );
}

// ── Opened letter — the real letter, re-set in a warm hand font ──
function OpenLetter({ font = '"Schoolbell", cursive' }) {
  const I = { red: '#C04432', orange: '#D8782A', green: '#5C8C3E', teal: '#2E847A', blue: '#3370A8', purple: '#7B5BB8', pink: '#C95E84', ink: '#3A2E18' };
  const W = ({ c, children }) => <span style={{ color: c }}>{children}</span>;
  return (
    <div style={{ position: 'relative', width: 760 }}>
      {/* Magic Sheep postage stamp in the margin */}
      <div style={{ position: 'absolute', top: -14, right: -10, zIndex: 4 }}>
        <div style={{ width: 104, height: 116, background: '#FBF6E8', border: '2px solid #8A7B5C', padding: 6, transform: 'rotate(6deg)', boxShadow: '0 4px 10px rgba(40,30,12,0.18)', textAlign: 'center', position: 'relative' }}>
          <svg width="100%" height="100%" viewBox="0 0 104 116" style={{ position: 'absolute', inset: 0 }} aria-hidden="true">{(() => { const d = []; for (let x = 4; x <= 100; x += 8) { d.push([x, 4]); d.push([x, 112]); } for (let y = 4; y <= 112; y += 8) { d.push([4, y]); d.push([100, y]); } return d.map((p, k) => <circle key={k} cx={p[0]} cy={p[1]} r="2" fill="#FBF6E8" stroke="#8A7B5C" strokeWidth="0.5" />); })()}</svg>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BlakeSheep size={84} p={DEREK} /></div>
          <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0, fontFamily: font, fontSize: 12, color: '#8A7B5C' }}>Magic Sheep</div>
        </div>
      </div>

      <div style={{
        background: 'repeating-linear-gradient(180deg,#FBF6E8 0 33px,#F4EBD2 33px 34px)',
        boxShadow: '0 16px 36px rgba(40,30,12,0.3)', padding: '38px 46px 44px', borderRadius: 4,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* red margin rule */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 64, width: 1.5, background: '#C0443255' }} />
        {/* little decorations */}
        <div style={{ position: 'absolute', top: 22, right: 130 }}><StarSticker size={20} color={I.pink} rotate={-10} /></div>
        <div style={{ position: 'absolute', bottom: 70, left: 24 }}><HeartSticker size={16} color={I.purple} /></div>

        <div style={{ fontFamily: font, fontSize: 25, lineHeight: 1.55, color: I.ink, paddingLeft: 28 }}>
          <div><W c={I.purple}>Dear</W> <span style={{ background: '#F0CD5A55', padding: '0 6px', borderRadius: 4 }}><W c={I.blue}>Isabella</W></span> and <span style={{ background: '#5C8C3E22', padding: '0 6px', borderRadius: 4 }}><W c={I.green}>Isaac</W></span> <StarSticker size={16} color={I.red} rotate={8} style={{ verticalAlign: 'middle' }} /></div>
          <div style={{ marginTop: 6 }}><W c={I.blue}><span style={{ fontSize: 32 }}>Baaaaaaaaa</span></W> <W c={I.orange}>from</W> <W c={I.purple}>the Magic Sheep!</W></div>
          <div style={{ marginTop: 12 }}><W c={I.teal}>Broadcasting live from our holiday location:</W> <span style={{ fontFamily: '"Patrick Hand",cursive', fontWeight: 700, letterSpacing: 1 }}>THE UNITED KINGDOM!</span></div>
          <div><W c={I.purple}>where the grass is surprisingly</W> <W c={I.green}>soggy</W> <W c={I.purple}>but very</W> <W c={I.orange}>tasty.</W></div>
          <div style={{ marginTop: 12 }}><W c={I.blue}>We hope you are having a</W> <W c={I.pink}>wonderful</W> <W c={I.blue}>year filled with adventures, fun, and</W> <W c={I.red}>absolutely no</W> <W c={I.blue}>accidental eating of magical live stock.</W></div>
          <div style={{ marginTop: 12 }}><W c={I.orange}>And on that topic — let's</W> <span style={{ fontFamily: '"Patrick Hand",cursive', fontWeight: 700, fontSize: 34, color: I.teal }}>SHEAR</span> <W c={I.orange}>the truth:</W></div>
          <div style={{ marginTop: 8, fontFamily: '"Patrick Hand",cursive', fontWeight: 700, fontSize: 32, color: I.green, lineHeight: 1.2 }}>YOU CAN EAT LAMB! <W c={I.red}>REALLY. OFFICIALLY.</W></div>
          <div style={{ marginTop: 14 }}>Three quick reasons, signed under hoof:
            <div style={{ marginTop: 4, paddingLeft: 8 }}>
              <div><W c={I.coral || I.red}>1.</W> Magic sheep cannot be eaten. <W c={I.purple}>We are protected by sparkles, snacks, and several official meadow rules.</W></div>
              <div style={{ marginTop: 2 }}><W c={I.blue}>2.</W> Ordinary lamb gives humans power for crafting, dancing, Lego, <W c={I.orange}>and telling adults they are wrong.</W></div>
              <div style={{ marginTop: 2 }}><W c={I.green}>3.</W> Last year's vote: 41 yes, 1 no. <W c={I.red}>(Derek. Again.)</W></div>
            </div>
          </div>
          <div style={{ marginTop: 16 }}><W c={I.teal}>with woolly love &amp; cloud cuddles,</W></div>
          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 34 }}>✿</span> <W c={I.red}><span style={{ fontSize: 38 }}>The Magic Sheep</span></W> <span style={{ fontSize: 16, color: '#8A7B5C' }}>(and Derek, who insists)</span>
            <span style={{ marginLeft: 'auto' }}><BlakeSheep size={132} p={{ sx: 1, sy: 1, mood: 'rainbow', eyes: 'happy' }} /></span>
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { MailboxRack, OpenLetter, MS_LETTERS });
// ── Tail: per-page renderers for the multi-page site. Each returns the page's
//    art as an HTML string; a `art` Stimulus controller mounts the right one,
//    and the HAML layout/views provide the navbar + page headings. (The old
//    single-page Board + draggable minimap have been removed.)

const SCENES = window.SCENES;
const SceneBg = window.SceneBg;
const StylePomPom = BlakeDefault; // prototype fallback (sheep-styles.jsx omitted)

// Centre wide fixed-width art on the page; allow horizontal scroll on narrow screens.
function Stage({ width, children }) {
  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <div style={{ width, maxWidth: "100%", margin: "0 auto" }}>{children}</div>
    </div>
  );
}

// Home — a featured adventure cover (the Alps) as the hero.
function renderHome() {
  const scene = SCENES.find((s) => s.id === "alps") || SCENES[0];
  return (
    <Stage width={1180}>
      <SceneBg scene={scene} width={1180} height={520} />
    </Stage>
  );
}

// Front covers — the ten adventures as a gallery (no live-swap buttons).
// The Flock — collector cards.
function renderFlock() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 18, justifyContent: "center" }}>
      {BLAKE_FLOCK.map((s) => <BlakeCard sheep={s} />)}
    </div>
  );
}

// The Mailbox — write → photograph → post → reply (with the upload drop-zone).
function renderMailbox() {
  return <Stage width={1080}><MailboxFlow sheepStyle={BlakeDefault} /></Stage>;
}

// Letters — the wax-sealed envelopes (click jumps to the opened letter) + the letter.
function renderLetters() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 48, alignItems: "center" }}>
      <Stage width={1080}>
        <a href="#opened-letter" style={{ display: "block", textDecoration: "none", cursor: "pointer" }}>
          <MailboxRack />
        </a>
      </Stage>
      <div id="opened-letter" style={{ scrollMarginTop: 90 }}>
        <Stage width={760}><OpenLetter /></Stage>
      </div>
    </div>
  );
}

const SECTIONS = {
  home: renderHome,
  flock: renderFlock,
  mailbox: renderMailbox,
  letters: renderLetters,
};

// Render a named section to an HTML string (used by the `art` Stimulus controller).
function renderSection(name) {
  const fn = SECTIONS[name];
  return fn ? fn().toString() : "";
}

export { renderSection, SECTIONS };
